import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  Stack,
  Autocomplete,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Card,
  CardContent,
  Link,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Visibility, 
  Cancel, 
  CheckCircle, 
  SwapHoriz,
  FileDownload,
  FilterList,
  ArrowUpward,
  ArrowDownward,
  CloudUpload,
  Delete,
  Image as ImageIcon,
  WhatsApp as WhatsAppIcon,
  UploadFile,
} from '@mui/icons-material';
import { requestService, Request } from '../../services/requestService';
import { TO_DROP_DOWN_OPTIONS } from '../../constants/toDropDownOptions';
import { serviceProviderService } from '../../services/serviceProviderService';
import UploadProgressDialog, { UploadProgressEvent } from '../../components/UploadProgressDialog';
import { handleSSEImport } from '../../utils/sseHelper';
import { insuranceClientService } from '../../services/insuranceClientService';
import { servicePackageService } from '../../services/servicePackageService';
import { insuranceCompanyService } from '../../services/insuranceCompanyService';
import CreateInvoiceModal from '../../components/CreateInvoiceModal';
import { NumberTextField } from '../../components/common';
import AddPaymentModal from '../../components/AddPaymentModal';
import api from '../../api/axios';
import * as XLSX from 'xlsx';
import { usePagination } from '../../hooks/usePagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import InvoiceService from '../../services/invoice.service';
import { addLogoToPDF } from '../../utils/pdfLogoHelper';
import { descriptionWithMapLink } from '../../utils/descriptionWithMapLink';
import { getReasonsFromRequestEntity } from '../../utils/requestReasons';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';

const RequestList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(25);
  const [requests, setRequests] = useState<Request[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<Request[]>([]);
  const [insuranceClients, setInsuranceClients] = useState<any[]>([]);
  const [serviceProviders, setServiceProviders] = useState<any[]>([]);

  // Helper function to format phone number for WhatsApp
  const formatPhoneForWhatsApp = (phone: string): string => {
    if (!phone) return '';
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    // If it doesn't start with +, add it
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    return cleaned;
  };

  // Helper function to open WhatsApp chat
  const openWhatsAppChat = (phone: string) => {
    const formattedPhone = formatPhoneForWhatsApp(phone);
    if (formattedPhone) {
      window.open(`https://wa.me/${formattedPhone.replace('+', '')}`, '_blank');
    }
  };

  // Helper function to get phone number from request
  // This extracts the phone number from various sources (insurance client, customer, etc.)
  const getRequestPhoneNumber = (request: Request): string | null => {
    const attrs = request.attributes || request;
    
    // Try to get phone from insurance client
    const insuranceClient = attrs.insuranceClient?.data?.attributes || 
                           attrs.insuranceClient?.data || 
                           attrs.insuranceClient?.attributes ||
                           attrs.insuranceClient;
    
    if (insuranceClient?.phoneNumber) {
      return insuranceClient.phoneNumber;
    }
    
    // Try to get phone from customer
    const customer = attrs.customer?.data?.attributes || 
                    attrs.customer?.data || 
                    attrs.customer?.attributes ||
                    attrs.customer;
    
    if (customer?.phone) {
      return customer.phone;
    }
    
    // If not found in populated data, try to find in insuranceClients array
    const insuranceClientId = attrs.insuranceClient?.data?.id || 
                             attrs.insuranceClient?.data?.attributes?.id || 
                             attrs.insuranceClient?.id || 
                             attrs.insuranceClient?.attributes?.id ||
                             (typeof attrs.insuranceClient === 'number' ? attrs.insuranceClient : null);
    
    if (insuranceClientId && insuranceClients.length > 0) {
      const foundClient = insuranceClients.find(client => {
        const clientId = client.id || client.attributes?.id || client.data?.id || client.data?.attributes?.id;
        return clientId === insuranceClientId || clientId === Number(insuranceClientId) || Number(clientId) === Number(insuranceClientId);
      });
      
      if (foundClient) {
        const clientAttrs = foundClient.attributes || foundClient.data?.attributes || foundClient.data || foundClient;
        if (clientAttrs?.phoneNumber) {
          return clientAttrs.phoneNumber;
        }
      }
    }
    
    return null;
  };

  // Helper function to parse Excel file
  const parseExcelFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          // IMPORTANT: Do NOT use cellDates:true - it applies timezone conversions!
          // Instead, read raw values (serial numbers) and let backend parse them
          const workbook = XLSX.read(data, { type: 'array', cellDates: false });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          // Use raw:true to get Excel serial numbers for dates
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: true }) as any[][];
          
          if (jsonData.length === 0) {
            reject(new Error('Excel file is empty'));
            return;
          }

          // First row is headers
          const headers = jsonData[0].map((h: any) => String(h || '').trim());
          
          // Find column indices
          const getColumnIndex = (name: string) => {
            return headers.findIndex((h: string) => 
              h.toLowerCase().includes(name.toLowerCase())
            );
          };

          const dateIdx = getColumnIndex('Date');
          const policyIdx = getColumnIndex('Policy number');
          const vehicleTypeIdx = getColumnIndex('Vehicle type');
          const vehicleModelIdx = getColumnIndex('Vehicle model');
          const plateIdx = getColumnIndex('Plate Number');
          const customerIdx = getColumnIndex('Customer Number');
          const locationFromIdx = getColumnIndex('Location (from)');
          const locationToIdx = getColumnIndex('Location (to)');
          const driverIdx = getColumnIndex('Driver Name');
          const serviceTypeIdx = getColumnIndex('Service type');
          const breakdownIdx = getColumnIndex('Breakdown details');
          const omrIdx = getColumnIndex('OMR');
          const registeredByIdx = getColumnIndex('REGISTERED BY') !== -1 ? getColumnIndex('REGISTERED BY') : getColumnIndex('REGISTRED BY');

          // Parse rows
          const importData = jsonData.slice(1).map((row: any[]) => {
            const getValue = (idx: number) => {
              if (idx >= 0 && idx < row.length) {
                const val = row[idx];
                return val !== null && val !== undefined ? String(val).trim() : null;
              }
              return null;
            };

            // Special handling for date column - preserve raw values
            const getDateValue = (idx: number) => {
              if (idx >= 0 && idx < row.length) {
                const val = row[idx];
                if (val === null || val === undefined) return null;
                
                // If it's a number (Excel serial number), return as string
                // The backend will parse Excel serial numbers correctly
                if (typeof val === 'number' && !isNaN(val)) {
                  console.log(`[EXCEL DATE] Serial number: ${val}`);
                  return String(val);
                }
                
                // If it's a Date object (shouldn't happen with cellDates:false, but just in case)
                if (val instanceof Date && !isNaN(val.getTime())) {
                  // Convert to Excel serial number to avoid timezone issues
                  const excelEpoch = new Date(1899, 11, 30);
                  const serialNumber = Math.floor((val.getTime() - excelEpoch.getTime()) / (24 * 60 * 60 * 1000));
                  console.log(`[EXCEL DATE] Date object converted to serial: ${serialNumber}`);
                  return String(serialNumber);
                }
                
                // Otherwise, return as string (handles text dates like "12-May-2025")
                const strVal = String(val).trim();
                if (strVal) {
                  console.log(`[EXCEL DATE] Text string: "${strVal}"`);
                }
                return strVal || null;
              }
              return null;
            };

            return {
              date: getDateValue(dateIdx),
              policyNumber: getValue(policyIdx),
              vehicleType: getValue(vehicleTypeIdx),
              vehicleModel: getValue(vehicleModelIdx),
              plateNumber: getValue(plateIdx),
              customerNumber: getValue(customerIdx),
              locationFrom: getValue(locationFromIdx),
              locationTo: getValue(locationToIdx),
              driverName: getValue(driverIdx),
              serviceType: getValue(serviceTypeIdx),
              breakdownDetails: getValue(breakdownIdx),
              omrAmount: omrIdx >= 0 ? getValue(omrIdx)?.replace('OMR', '').replace(/\s/g, '') : null,
              registeredBy: getValue(registeredByIdx),
            };
          }).filter(row => row.date || row.policyNumber || row.customerNumber); // Filter empty rows

          resolve(importData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  // Helper function to parse text data (existing logic)
  const parseTextData = (data: string): any[] => {
    const lines = data.split('\n').filter(line => line.trim());
    const headers = lines[0]?.split('\t') || [];
    
    // Find column indices
    const getColumnIndex = (name: string) => {
      return headers.findIndex((h: string) => 
        h.toLowerCase().includes(name.toLowerCase())
      );
    };

    const dateIdx = getColumnIndex('Date');
    const policyIdx = getColumnIndex('Policy number');
    const vehicleTypeIdx = getColumnIndex('Vehicle type');
    const vehicleModelIdx = getColumnIndex('Vehicle model');
    const plateIdx = getColumnIndex('Plate Number');
    const customerIdx = getColumnIndex('Customer Number');
    const locationFromIdx = getColumnIndex('Location (from)');
    const locationToIdx = getColumnIndex('Location (to)');
    const driverIdx = getColumnIndex('Driver Name');
    const serviceTypeIdx = getColumnIndex('Service type');
    const breakdownIdx = getColumnIndex('Breakdown details');
    const omrIdx = getColumnIndex('OMR');
    const registeredByIdx = getColumnIndex('REGISTERED BY') !== -1 ? getColumnIndex('REGISTERED BY') : getColumnIndex('REGISTRED BY');

    // Parse rows
    const importData = lines.slice(1).map((line) => {
      const cols = line.split('\t');
      return {
        date: dateIdx >= 0 ? cols[dateIdx]?.trim() : null,
        policyNumber: policyIdx >= 0 ? cols[policyIdx]?.trim() : null,
        vehicleType: vehicleTypeIdx >= 0 ? cols[vehicleTypeIdx]?.trim() : null,
        vehicleModel: vehicleModelIdx >= 0 ? cols[vehicleModelIdx]?.trim() : null,
        plateNumber: plateIdx >= 0 ? cols[plateIdx]?.trim() : null,
        customerNumber: customerIdx >= 0 ? cols[customerIdx]?.trim() : null,
        locationFrom: locationFromIdx >= 0 ? cols[locationFromIdx]?.trim() : null,
        locationTo: locationToIdx >= 0 ? cols[locationToIdx]?.trim() : null,
        driverName: driverIdx >= 0 ? cols[driverIdx]?.trim() : null,
        serviceType: serviceTypeIdx >= 0 ? cols[serviceTypeIdx]?.trim() : null,
        breakdownDetails: breakdownIdx >= 0 ? cols[breakdownIdx]?.trim() : null,
        omrAmount: omrIdx >= 0 ? cols[omrIdx]?.trim()?.replace('OMR', '').replace(/\s/g, '') : null,
        registeredBy: registeredByIdx >= 0 ? cols[registeredByIdx]?.trim() : null,
      };
    }).filter(row => row.date || row.policyNumber || row.customerNumber); // Filter empty rows

    return importData;
  };

  const [servicePackages, setServicePackages] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<Request | null>(null);
  const [viewingRequest, setViewingRequest] = useState<Request | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [vinNumber, setVinNumber] = useState<string>('');
  const [selectedInsuranceClient, setSelectedInsuranceClient] = useState<any>(null);
  const [clientPackageInfo, setClientPackageInfo] = useState<any>(null);
  const [checkingVin, setCheckingVin] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningRequest, setAssigningRequest] = useState<Request | null>(null);
  const [providerToAssign, setProviderToAssign] = useState<any>(null);
  const [changeProviderOpen, setChangeProviderOpen] = useState(false);
  const [changingRequest, setChangingRequest] = useState<Request | null>(null);
  const [newProvider, setNewProvider] = useState<any>(null);
  // Automatically show filters if URL has filter parameters
  const [showFilters, setShowFilters] = useState(
    searchParams.has('dateFrom') || searchParams.has('dateTo')
  );
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [requestForInvoice, setRequestForInvoice] = useState<Request | null>(null);
  const [requestForPayment, setRequestForPayment] = useState<Request | null>(null);
  const [customerInvoiceItems, setCustomerInvoiceItems] = useState<any[]>([]);
  const [insuranceInvoiceItems, setInsuranceInvoiceItems] = useState<any[]>([]);
  const [editInvoiceItemModalOpen, setEditInvoiceItemModalOpen] = useState(false);
  const [editingInvoiceItem, setEditingInvoiceItem] = useState<any>(null);
  const [editingInvoiceType, setEditingInvoiceType] = useState<'customer' | 'insurance' | null>(null);
  const [editItemFormData, setEditItemFormData] = useState({
    description: '',
    quantity: 1,
    unitPrice: 0,
    discount: 0,
    tax: 0,
    total: 0,
    apply5PercentTax: false
  });
  const [savingInvoiceItem, setSavingInvoiceItem] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [importDataText, setImportDataText] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'text' | 'excel'>('text');
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const progressDialogRef = useRef<any>(null);
  // Assign client modal state
  const [assignClientModalOpen, setAssignClientModalOpen] = useState(false);
  const [assigningRequestForClient, setAssigningRequestForClient] = useState<Request | null>(null);
  const [selectedClientToAssign, setSelectedClientToAssign] = useState<any>(null);
  const [creatingNewClient, setCreatingNewClient] = useState(false);
  const [newClientFormData, setNewClientFormData] = useState({
    vinNumber: '',
    customerName: '',
    phoneNumber: '',
    email: '',
    plateNumber: '',
    carMake: '',
    carModel: '',
    carYear: '',
    policyNumber: '',
    policyStartDate: '',
    policyEndDate: '',
    servicePackage: '',
    insuranceCompany: '',
    notes: '',
    isActive: true
  });
  const [insuranceCompanies, setInsuranceCompanies] = useState<any[]>([]);
  const [assigningClient, setAssigningClient] = useState(false);
  
  // Tab state: 0: All, 1: Today, 2: Incomplete. Sync from URL so /requests?tab=today opens Today tab.
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl === 'today' ? 1 : tabFromUrl === 'incomplete' ? 2 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);

  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || '',
    searchTerm: '',
    serviceProvider: 'all',
    servicePackage: 'all'
  });
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [formData, setFormData] = useState({
    insuranceClient: '',
    serviceProvider: '',
    description: '',
    scheduledDate: '',
    notes: '',
    serviceType: '',
    serviceCode: '',
    phoneNumber: ''
  });
  const [beforePhotoFile, setBeforePhotoFile] = useState<File | null>(null);
  const [beforePhotoPreview, setBeforePhotoPreview] = useState<string | null>(null);
  const [afterPhotoFile, setAfterPhotoFile] = useState<File | null>(null);
  const [afterPhotoPreview, setAfterPhotoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imageErrors, setImageErrors] = useState<{ before: boolean; after: boolean }>({ before: false, after: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAll = async () => {
      await Promise.all([
        loadInsuranceClients(),
        loadServicePackages(),
        loadInsuranceCompanies()
      ]);
      // Requests will be loaded by the pagination useEffect
      // Service providers will be loaded lazily when filters are shown
    };
    loadAll();
  }, []);

  // Sync activeTab from URL when navigating with ?tab=today or ?tab=incomplete (e.g. from Operations Overview)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const tabIndex = tabParam === 'today' ? 1 : tabParam === 'incomplete' ? 2 : 0;
    setActiveTab(tabIndex);
  }, [searchParams]);

  // Lazy load service providers when filters are shown
  useEffect(() => {
    if (showFilters && serviceProviders.length === 0) {
      loadServiceProviders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters]);

  const loadInsuranceCompanies = async () => {
    try {
      const response = await insuranceCompanyService.getAll();
      const companiesData = Array.isArray(response) ? response : (response?.data || []);
      setInsuranceCompanies(companiesData);
    } catch (error) {
      console.error('Error loading insurance companies:', error);
      setInsuranceCompanies([]);
    }
  };

  // Reset to first page when filters, tabs, or sorting change
  useEffect(() => {
    if (pagination.page !== 0) {
      setPagination({ page: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortField, sortOrder, activeTab]);

  // Load requests when pagination, filters, tabs, or sorting changes
  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, filters, sortField, sortOrder, activeTab]);

  useEffect(() => {
    applyFilters();
  }, [requests]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading requests - Page:', pagination.page + 1, 'PageSize:', pagination.pageSize, 'Tab:', activeTab);
      
      // Build query parameters with nested population for filtering
      const params: any = {
        sort: `${sortField}:${sortOrder}`,
        'pagination[page]': pagination.page + 1, // Strapi uses 1-based indexing
        'pagination[pageSize]': pagination.pageSize,
        'populate[insuranceClient][populate][servicePackage]': '*',
        'populate[serviceProvider]': '*',
        'populate[invoice]': '*',
        'populate[invoices]': '*',
        'populate[previousServiceProvider]': '*',
        'populate[evidencePhotoBefore]': '*',
        'populate[evidencePhotoAfter]': '*'
      };

      // Apply tab-based filters
      if (activeTab === 1) {
        // Today tab - filter by today's date
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);
        params['filters[createdAt][$gte]'] = today.toISOString();
        params['filters[createdAt][$lte]'] = todayEnd.toISOString();
      } else if (activeTab === 2) {
        // Incomplete tab - filter out completed and cancelled statuses
        params['filters[status][$notIn][0]'] = 'completed';
        params['filters[status][$notIn][1]'] = 'cancelled';
      }

      // Add filters (only apply status filter if not in Incomplete tab)
      if (filters.status !== 'all' && activeTab !== 2) {
        params['filters[status][$eq]'] = filters.status;
      }

      // Date filters (only apply if not in Today tab)
      if (activeTab !== 1) {
        if (filters.dateFrom) {
          params['filters[createdAt][$gte]'] = new Date(filters.dateFrom).toISOString();
        }

        if (filters.dateTo) {
          const dateTo = new Date(filters.dateTo);
          dateTo.setHours(23, 59, 59, 999); // End of day
          params['filters[createdAt][$lte]'] = dateTo.toISOString();
        }
      }

      console.log('🔍 RequestList: Fetching with filters:', {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        dateFromISO: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : null,
        dateToISO: filters.dateTo ? new Date(filters.dateTo + 'T23:59:59.999Z').toISOString() : null,
        status: filters.status,
      });

      // Search across multiple fields including nested relations
      if (filters.searchTerm) {
        const searchTerm = filters.searchTerm.trim();
        params['filters[$or][0][requestNumber][$containsi]'] = searchTerm;
        params['filters[$or][1][description][$containsi]'] = searchTerm;
        params['filters[$or][2][phoneNumber][$containsi]'] = searchTerm;
        params['filters[$or][3][serviceType][$containsi]'] = searchTerm;
        params['filters[$or][4][serviceCode][$containsi]'] = searchTerm;
        // Search in nested insurance client fields
        params['filters[$or][5][insuranceClient][customerName][$containsi]'] = searchTerm;
        params['filters[$or][6][insuranceClient][vinNumber][$containsi]'] = searchTerm;
        params['filters[$or][7][insuranceClient][phoneNumber][$containsi]'] = searchTerm;
        params['filters[$or][8][insuranceClient][policyNumber][$containsi]'] = searchTerm;
        // Search in nested service provider fields
        params['filters[$or][9][serviceProvider][name][$containsi]'] = searchTerm;
        params['filters[$or][10][serviceProvider][phone][$containsi]'] = searchTerm;
      }

      if (filters.serviceProvider !== 'all') {
        params['filters[serviceProvider][id][$eq]'] = filters.serviceProvider;
      }

      if (filters.servicePackage !== 'all') {
        params['filters[insuranceClient][servicePackage][id][$eq]'] = filters.servicePackage;
      }

      const response = await requestService.getList(params);
      const requestsData = response.data?.data || response.data || [];
      const paginationMeta = response.data?.meta?.pagination || response.meta?.pagination || {};
      
      console.log('📊 RequestList: Received', requestsData.length, 'requests, total:', paginationMeta.total);
      if (requestsData.length > 0) {
        console.log('Sample request createdAt:', requestsData[0]?.attributes?.createdAt);
      }
      
      console.log('Loaded requests:', requestsData.length, 'Total:', paginationMeta.total);
      setRequests(requestsData);
      
      // Update pagination metadata
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error: any) {
      console.error('Error loading requests:', error);
      setError(error.response?.data?.error?.message || 'Failed to load requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadInsuranceClients = async () => {
    try {
      const response = await insuranceClientService.getAll({
        'pagination[pageSize]': 10000,
      });
      const clients = response.data || [];
      console.log('Loaded insurance clients:', clients.length, clients.slice(0, 2));
      setInsuranceClients(clients);
    } catch (error) {
      console.error('Error loading insurance clients:', error);
    }
  };

  const loadServiceProviders = async () => {
    try {
      const response = await serviceProviderService.getAll({
        'pagination[pageSize]': 10000,
      });
      const providers = response.data || [];
      // Sort: active first, then by name
      const sorted = providers.sort((a: any, b: any) => {
        const aActive = a.attributes?.isActive ?? a.isActive ?? false;
        const bActive = b.attributes?.isActive ?? b.isActive ?? false;
        if (aActive !== bActive) {
          return aActive ? -1 : 1;
        }
        const aName = a.attributes?.name || a.name || '';
        const bName = b.attributes?.name || b.name || '';
        return aName.localeCompare(bName);
      });
      setServiceProviders(sorted);
    } catch (error) {
      console.error('Error loading service providers:', error);
    }
  };

  const formatSchedule = (schedule: any): string => {
    if (!schedule || typeof schedule !== 'object') return 'No schedule';
    
    const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const activeDays = DAYS.filter(day => schedule[day]?.active);
    
    if (activeDays.length === 0) return 'No schedule';
    if (activeDays.length === 7) {
      // Check if all days have same hours
      const firstDay = schedule[activeDays[0]];
      const allSame = activeDays.every(day => 
        schedule[day]?.from === firstDay?.from && schedule[day]?.to === firstDay?.to
      );
      if (allSame && firstDay?.from && firstDay?.to) {
        return `${firstDay.from}-${firstDay.to} (All days)`;
      }
    }
    
    // Show first few active days with times
    const displayDays = activeDays.slice(0, 2);
    const times = displayDays.map(day => {
      const daySchedule = schedule[day];
      if (daySchedule?.from && daySchedule?.to) {
        return `${day.substring(0, 3)} ${daySchedule.from}-${daySchedule.to}`;
      }
      return day.substring(0, 3);
    }).join(', ');
    
    if (activeDays.length > 2) {
      return `${times} +${activeDays.length - 2} more`;
    }
    return times;
  };

  const loadServicePackages = async () => {
    try {
      const response = await servicePackageService.getAll();
      const packagesData = response?.data || response || [];
      setServicePackages(Array.isArray(packagesData) ? packagesData : []);
    } catch (error) {
      console.error('Error loading service packages:', error);
    }
  };

  const applyFilters = () => {
    // Since filtering and sorting are now done server-side,
    // we just set filteredRequests to the requests from the server
    setFilteredRequests(requests);
  };

  const extractFirstUrl = (text?: string | null): string | null => {
    if (!text || typeof text !== 'string') return null;
    const match = text.match(/https?:\/\/[^\s)]+/i);
    return match ? match[0] : null;
  };

  const getRequestLocationHref = (attrs: any): string | null => {
    const explicit =
      extractFirstUrl(attrs?.locationText) ||
      extractFirstUrl(attrs?.description) ||
      extractFirstUrl(attrs?.notes);
    if (explicit) return explicit;

    const lat = attrs?.locationLatitude;
    const lng = attrs?.locationLongitude;
    if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }
    return null;
  };

  const handleOpen = async (request?: Request) => {
    if (request) {
      setEditingRequest(request);
      
      const attrs = request.attributes || request;
      const insuranceClientId = attrs.insuranceClient?.data?.id || attrs.insuranceClient?.id;
      
      if (insuranceClientId) {
        // Insurance client
        try {
          const client = await insuranceClientService.getOne(insuranceClientId);
          const clientAttrs = client.attributes || client;
          setSelectedInsuranceClient(clientAttrs);
          setVinNumber(clientAttrs.vinNumber || '');
          // Fetch package info
          const result = await insuranceClientService.checkVin(clientAttrs.vinNumber);
          setClientPackageInfo(result);
        } catch (error) {
          console.error('Error loading insurance client:', error);
        }
      } else {
        setSelectedInsuranceClient(null);
        setClientPackageInfo(null);
        setVinNumber('');
      }
      
      const providerId = attrs.serviceProvider?.data?.id || attrs.serviceProvider?.id;
      const provider = serviceProviders.find(p => p.id === providerId);
      setSelectedProvider(provider || null);
      
      setFormData({
        insuranceClient: insuranceClientId || '',
        serviceProvider: providerId || '',
        description: attrs.description || '',
        scheduledDate: attrs.scheduledDate ? attrs.scheduledDate.split('T')[0] : '',
        notes: attrs.notes || '',
        serviceType: attrs.serviceType || '',
        serviceCode: attrs.serviceCode || '',
        phoneNumber: attrs.phoneNumber || ''
      });
      
      // Load existing photos
      const beforePhoto = attrs.evidencePhotoBefore?.data || attrs.evidencePhotoBefore;
      const afterPhoto = attrs.evidencePhotoAfter?.data || attrs.evidencePhotoAfter;
      if (beforePhoto) {
        setBeforePhotoPreview(beforePhoto.attributes?.url || beforePhoto.url || null);
      } else {
        setBeforePhotoPreview(null);
      }
      if (afterPhoto) {
        setAfterPhotoPreview(afterPhoto.attributes?.url || afterPhoto.url || null);
      } else {
        setAfterPhotoPreview(null);
      }
      setBeforePhotoFile(null);
      setAfterPhotoFile(null);
    } else {
      setEditingRequest(null);
      setSelectedProvider(null);
      setVinNumber('');
      setSelectedInsuranceClient(null);
      setClientPackageInfo(null);
      setFormData({
        insuranceClient: '',
        serviceProvider: '',
        description: '',
        scheduledDate: '',
        notes: '',
        serviceType: '',
        serviceCode: '',
        phoneNumber: ''
      });
      setBeforePhotoFile(null);
      setBeforePhotoPreview(null);
      setAfterPhotoFile(null);
      setAfterPhotoPreview(null);
    }
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingRequest(null);
    setVinNumber('');
    setSelectedInsuranceClient(null);
    setClientPackageInfo(null);
    setError('');
    setBeforePhotoFile(null);
    setBeforePhotoPreview(null);
    setAfterPhotoFile(null);
    setAfterPhotoPreview(null);
  };

  const handleCheckVin = async () => {
    if (!vinNumber.trim()) {
      setError('Please enter a VIN number');
      return;
    }

    try {
      setCheckingVin(true);
      setError('');
      const result = await insuranceClientService.checkVin(vinNumber.trim());
      
      if (result.found) {
        // Check if policy is active
        if (!result.policy?.isActive) {
          setError('Client policy has expired or is inactive. Cannot create requests.');
          setSelectedInsuranceClient(null);
          setClientPackageInfo(null);
          return;
        }

        // Check if insurance client has a service package
        if (!result.client?.servicePackage) {
          setError('Insurance client does not have a service package assigned. Cannot create requests. Please assign a service package first.');
          setSelectedInsuranceClient(null);
          setClientPackageInfo(null);
          return;
        }

        // Check if there are any available services
        const availableServices = result.serviceUsage?.services?.filter((s: any) => s.canUseAgain && s.remainingCount > 0);
        if (!availableServices || availableServices.length === 0) {
          setError('No services available. All service limits have been reached for this client.');
          setSelectedInsuranceClient(null);
          setClientPackageInfo(null);
          return;
        }

        setSelectedInsuranceClient(result.client);
        setClientPackageInfo(result);
        setFormData({ ...formData, insuranceClient: result.client.id });
        
        // Reset service selection when VIN changes
        setFormData(prev => ({ ...prev, serviceCode: '', serviceType: '' }));
      } else {
        setError('Client with this VIN not found');
        setSelectedInsuranceClient(null);
        setClientPackageInfo(null);
      }
    } catch (error: any) {
      console.error('Error checking VIN:', error);
      setError(error.response?.data?.error?.message || 'Failed to check VIN number');
      setSelectedInsuranceClient(null);
      setClientPackageInfo(null);
    } finally {
      setCheckingVin(false);
    }
  };

  const uploadFile = async (file: File): Promise<number | null> => {
    const formData = new FormData();
    formData.append('files', file);

    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data && response.data[0]) {
        return response.data[0].id;
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
    return null;
  };

  // Helper function to get image URL and check if it's valid
  const getImageUrl = (photoData: any): string | null => {
    if (!photoData) return null;

    let url = photoData?.data?.attributes?.url ||
              photoData?.url ||
              photoData?.data?.url ||
              photoData?.attributes?.url ||
              '';

    // Check if URL is valid (not empty, not null, not undefined)
    if (!url || typeof url !== 'string' || url.trim() === '' || url === 'null' || url === 'undefined') {
      return null;
    }

    // Strapi returns relative paths (e.g. /uploads/...); prepend API base so the image loads from the backend
    if (url.startsWith('/')) {
      const API_URL = (process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '');
      url = `${API_URL}${url}`;
    }

    return url;
  };

  // Helper function to check if image data exists and has valid URL
  const hasValidImage = (photoData: any): boolean => {
    if (!photoData) return false;
    const url = getImageUrl(photoData);
    return url !== null && url !== '';
  };

  // Handle admin upload of before photo (in view mode)
  const handleAdminBeforePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !viewingRequest) return;

    try {
      setUploading(true);
      const photoId = await uploadFile(file);
      if (photoId) {
        await requestService.update(viewingRequest.id, { evidencePhotoBefore: photoId } as any);
        await reloadRequestDetails(viewingRequest.id);
      }
    } catch (error) {
      console.error('Error uploading before photo:', error);
      setError('Failed to upload before photo');
    } finally {
      setUploading(false);
      // Reset input
      if (e.target) e.target.value = '';
    }
  };

  // Handle admin upload of after photo (in view mode)
  const handleAdminAfterPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !viewingRequest) return;

    try {
      setUploading(true);
      const photoId = await uploadFile(file);
      if (photoId) {
        await requestService.update(viewingRequest.id, { evidencePhotoAfter: photoId } as any);
        await reloadRequestDetails(viewingRequest.id);
      }
    } catch (error) {
      console.error('Error uploading after photo:', error);
      setError('Failed to upload after photo');
    } finally {
      setUploading(false);
      // Reset input
      if (e.target) e.target.value = '';
    }
  };

  const handleBeforePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBeforePhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBeforePhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAfterPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAfterPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAfterPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBeforePhoto = () => {
    setBeforePhotoFile(null);
    setBeforePhotoPreview(null);
  };

  const removeAfterPhoto = () => {
    setAfterPhotoFile(null);
    setAfterPhotoPreview(null);
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);
      if (!formData.insuranceClient) {
        setError('Please check VIN number for insurance client');
        return;
      }

      // Validate service package exists
      if (!clientPackageInfo?.client?.servicePackage) {
        setError('Insurance client must have a service package assigned to create requests.');
        return;
      }
      if (!clientPackageInfo?.serviceUsage?.services || clientPackageInfo.serviceUsage.services.length === 0) {
        setError('Insurance client service package has no services available.');
        return;
      }

      // Require service code
      if (!formData.serviceCode) {
        setError('Please select a service type from the available services');
        return;
      }

      // Validate that selected service is in the package and has available count
      const selectedService = clientPackageInfo?.serviceUsage?.services?.find(
        (s: any) => s.serviceCode === formData.serviceCode
      );
      
      if (!selectedService) {
        setError('Selected service is not available in the service package.');
        return;
      }

      if (!selectedService.canUseAgain || selectedService.remainingCount <= 0) {
        setError('Selected service has no remaining uses available.');
        return;
      }

      if (!formData.description) {
        setError('Please enter a description');
        return;
      }

      if (!formData.phoneNumber || formData.phoneNumber.trim() === '') {
        setError('Please enter a phone number');
        return;
      }

      // Upload files if new ones are selected
      let beforePhotoId: number | null = null;
      let afterPhotoId: number | null = null;

      if (beforePhotoFile) {
        beforePhotoId = await uploadFile(beforePhotoFile);
        if (!beforePhotoId) {
          setError('Failed to upload before photo');
          setUploading(false);
          return;
        }
      } else if (beforePhotoPreview && editingRequest) {
        // Keep existing photo if not changed
        const attrs = editingRequest.attributes || editingRequest;
        const existingBefore = attrs.evidencePhotoBefore?.data || attrs.evidencePhotoBefore;
        if (existingBefore) {
          beforePhotoId = existingBefore.id || existingBefore.attributes?.id;
        }
      }

      if (afterPhotoFile) {
        afterPhotoId = await uploadFile(afterPhotoFile);
        if (!afterPhotoId) {
          setError('Failed to upload after photo');
          setUploading(false);
          return;
        }
      } else if (afterPhotoPreview && editingRequest) {
        // Keep existing photo if not changed
        const attrs = editingRequest.attributes || editingRequest;
        const existingAfter = attrs.evidencePhotoAfter?.data || attrs.evidencePhotoAfter;
        if (existingAfter) {
          afterPhotoId = existingAfter.id || existingAfter.attributes?.id;
        }
      }

      const dataToSubmit: any = {
        ...formData,
        status: 'pending' as const
      };

      if (beforePhotoId !== null) {
        dataToSubmit.evidencePhotoBefore = beforePhotoId;
      }
      if (afterPhotoId !== null) {
        dataToSubmit.evidencePhotoAfter = afterPhotoId;
      }
      
      if (editingRequest) {
        await requestService.update(editingRequest.id, dataToSubmit);
      } else {
        await requestService.create(dataToSubmit);
      }
      loadRequests();
      handleClose();
    } catch (error: any) {
      console.error('Error saving request:', error);
      setError(error.response?.data?.error?.message || 'Failed to save request');
    } finally {
      setUploading(false);
    }
  };

  const handleViewDetails = async (request: Request) => {
    try {
      const response = await requestService.getById(request.id);
      setViewingRequest(response.data);
      setImageErrors({ before: false, after: false });
      setDetailsOpen(true);
      // Fetch invoice items
      await fetchInvoiceItems(response.data);
    } catch (error) {
      console.error('Error loading request details:', error);
    }
  };

  const fetchInvoiceItems = async (request: any) => {
    try {
      const requestAttrs = request.attributes || request;
      const invoices = requestAttrs.invoices?.data || requestAttrs.invoices || [];
      
      // Find customer and insurance invoices
      const customerInvoice = invoices.find((inv: any) => {
        const invAttrs = inv.attributes || inv;
        return invAttrs.invoiceType === 'service_provider_to_darb';
      });
      const insuranceInvoice = invoices.find((inv: any) => {
        const invAttrs = inv.attributes || inv;
        return invAttrs.invoiceType === 'insurance_company_monthly';
      });
      
      // Fetch items for customer invoice
      if (customerInvoice) {
        const customerInvoiceId = customerInvoice.id || customerInvoice.attributes?.id;
        if (customerInvoiceId) {
          try {
            const itemsResponse = await InvoiceService.getItems(customerInvoiceId);
            setCustomerInvoiceItems(itemsResponse.data?.data || []);
          } catch (err) {
            console.error('Error fetching customer invoice items:', err);
            setCustomerInvoiceItems([]);
          }
        }
      } else {
        setCustomerInvoiceItems([]);
      }
      
      // Fetch items for insurance invoice
      if (insuranceInvoice) {
        const insuranceInvoiceId = insuranceInvoice.id || insuranceInvoice.attributes?.id;
        if (insuranceInvoiceId) {
          try {
            const itemsResponse = await InvoiceService.getItems(insuranceInvoiceId);
            setInsuranceInvoiceItems(itemsResponse.data?.data || []);
          } catch (err) {
            console.error('Error fetching insurance invoice items:', err);
            setInsuranceInvoiceItems([]);
          }
        }
      } else {
        setInsuranceInvoiceItems([]);
      }
    } catch (error) {
      console.error('Error fetching invoice items:', error);
      setCustomerInvoiceItems([]);
      setInsuranceInvoiceItems([]);
    }
  };

  const reloadRequestDetails = async (requestId: number) => {
    try {
      const response = await requestService.getById(requestId);
      setViewingRequest(response.data);
      setImageErrors({ before: false, after: false });
      loadRequests();
      // Fetch invoice items
      await fetchInvoiceItems(response.data);
    } catch (error) {
      console.error('Error reloading request details:', error);
    }
  };

  // Invoice Item Edit Functions
  const handleEditInvoiceItem = (item: any, invoiceType: 'customer' | 'insurance') => {
    const itemAttrs = item.attributes || item;
    const subtotal = (itemAttrs.quantity || 1) * (itemAttrs.unitPrice || 0);
    const afterDiscount = subtotal - (itemAttrs.discount || 0);
    // Check if tax is approximately 5% of afterDiscount
    const calculatedTax5Percent = afterDiscount * 0.05;
    const hasTax = itemAttrs.tax || 0;
    const isTax5Percent = Math.abs(hasTax - calculatedTax5Percent) < 0.01;
    
    setEditingInvoiceItem(item);
    setEditingInvoiceType(invoiceType);
    setEditItemFormData({
      description: itemAttrs.description || '',
      quantity: itemAttrs.quantity || 1,
      unitPrice: itemAttrs.unitPrice || 0,
      discount: itemAttrs.discount || 0,
      tax: itemAttrs.tax || 0,
      total: itemAttrs.total || 0,
      apply5PercentTax: isTax5Percent
    });
    setEditInvoiceItemModalOpen(true);
  };

  const handleAddInvoiceItem = (invoiceType: 'customer' | 'insurance') => {
    setEditingInvoiceItem(null);
    setEditingInvoiceType(invoiceType);
    setEditItemFormData({
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      tax: 0,
      total: 0,
      apply5PercentTax: false
    });
    setEditInvoiceItemModalOpen(true);
  };

  const calculateItemTotal = () => {
    const { quantity, unitPrice, discount, apply5PercentTax } = editItemFormData;
    const subtotal = quantity * unitPrice;
    const afterDiscount = subtotal - (discount || 0);
    const tax = apply5PercentTax ? afterDiscount * 0.05 : (editItemFormData.tax || 0);
    const total = afterDiscount + tax;
    return { total, tax, subtotal, afterDiscount };
  };

  const handleSaveInvoiceItem = async () => {
    try {
      setSavingInvoiceItem(true);
      
      // Get the invoice ID based on type
      const requestAttrs = viewingRequest?.attributes || viewingRequest;
      const invoices = requestAttrs?.invoices?.data || requestAttrs?.invoices || [];
      
      const invoice = invoices.find((inv: any) => {
        const invAttrs = inv.attributes || inv;
        return editingInvoiceType === 'customer' 
          ? invAttrs.invoiceType === 'service_provider_to_darb'
          : invAttrs.invoiceType === 'insurance_company_monthly';
      });
      
      if (!invoice) {
        alert('Invoice not found');
        return;
      }
      
      const invoiceId = invoice.id || invoice.attributes?.id;
      const { total, tax } = calculateItemTotal();
      
      const itemData = {
        description: editItemFormData.description,
        quantity: editItemFormData.quantity,
        unitPrice: editItemFormData.unitPrice,
        discount: editItemFormData.discount,
        tax: tax,
        total: total,
        invoice: invoiceId
      };
      
      if (editingInvoiceItem) {
        // Update existing item
        const itemId = editingInvoiceItem.id || editingInvoiceItem.attributes?.id;
        await InvoiceService.updateItem(itemId, itemData);
      } else {
        // Create new item
        await InvoiceService.createItem(itemData);
      }
      
      // Reload invoice items
      if (viewingRequest) {
        await fetchInvoiceItems(viewingRequest);
        
        // Also reload the request to update totals
        const requestId = viewingRequest.id || viewingRequest.attributes?.id;
        if (requestId) {
          await reloadRequestDetails(requestId);
        }
      }
      
      setEditInvoiceItemModalOpen(false);
      setEditingInvoiceItem(null);
      setEditingInvoiceType(null);
    } catch (error) {
      console.error('Error saving invoice item:', error);
      alert('Error saving invoice item');
    } finally {
      setSavingInvoiceItem(false);
    }
  };

  const handleDeleteInvoiceItem = async (item: any, invoiceType: 'customer' | 'insurance') => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }
    
    try {
      const itemId = item.id || item.attributes?.id;
      await InvoiceService.deleteItem(itemId);
      
      // Reload invoice items
      if (viewingRequest) {
        await fetchInvoiceItems(viewingRequest);
        
        // Also reload the request to update totals
        const requestId = viewingRequest.id || viewingRequest.attributes?.id;
        if (requestId) {
          await reloadRequestDetails(requestId);
        }
      }
    } catch (error) {
      console.error('Error deleting invoice item:', error);
      alert('Error deleting invoice item');
    }
  };

  useEffect(() => {
    if (editInvoiceItemModalOpen) {
      const { total, tax } = calculateItemTotal();
      setEditItemFormData(prev => ({ ...prev, total, tax }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItemFormData.quantity, editItemFormData.unitPrice, editItemFormData.discount, editItemFormData.apply5PercentTax]);

  const handleDownloadInvoicePDF = async (invoiceType: 'customer' | 'insurance' = 'customer') => {
    if (!viewingRequest) return;
    
    try {
      // Get the request with invoices populated
      const requestAttrs = viewingRequest.attributes || viewingRequest;
      const invoices = requestAttrs.invoices?.data || requestAttrs.invoices || [];
      
      // Find the correct invoice based on invoiceType
      let targetInvoice = null;
      if (invoiceType === 'customer') {
        // Customer invoice: invoiceType should be 'service_provider_to_darb'
        targetInvoice = invoices.find((inv: any) => {
          const invAttrs = inv.attributes || inv;
          return invAttrs.invoiceType === 'service_provider_to_darb';
        });
      } else {
        // Insurance invoice: invoiceType should be 'insurance_company_monthly'
        targetInvoice = invoices.find((inv: any) => {
          const invAttrs = inv.attributes || inv;
          return invAttrs.invoiceType === 'insurance_company_monthly';
        });
      }
      
      // Fallback to old single invoice relation for backward compatibility
      if (!targetInvoice) {
        targetInvoice = viewingRequest.invoice?.data || viewingRequest.attributes?.invoice?.data;
      }
      
      if (!targetInvoice) {
        console.error(`No ${invoiceType} invoice found for this request`);
        return;
      }
      
      const invoiceId = targetInvoice.id || targetInvoice.attributes?.id;
      if (!invoiceId) return;

      const invoiceResponse = await InvoiceService.getOne(invoiceId, {
        populate: ['customer', 'insuranceClient.insuranceCompany', 'relatedRequests', 'request'],
      });
      const invoice = invoiceResponse.data?.data;
      if (!invoice) {
        console.error(`Invoice ${invoiceId} not found`);
        return;
      }
      
      // Verify we got the correct invoice type
      const invoiceData = invoice.attributes || invoice;
      if (invoiceType === 'customer' && invoiceData.invoiceType !== 'service_provider_to_darb') {
        console.error(`Wrong invoice type! Expected service_provider_to_darb, got ${invoiceData.invoiceType}`);
      }
      if (invoiceType === 'insurance' && invoiceData.invoiceType !== 'insurance_company_monthly') {
        console.error(`Wrong invoice type! Expected insurance_company_monthly, got ${invoiceData.invoiceType}`);
      }

      // Fetch line items
      const itemsResponse = await InvoiceService.getItems(invoiceId);
      const lineItems = itemsResponse.data?.data || [];

      const doc = new jsPDF();
      const data = invoice.attributes || invoice;
      const insuranceClient = data.insuranceClient?.data?.attributes || data.customer?.data?.attributes;
      const insuranceCompany = insuranceClient?.insuranceCompany?.data?.attributes || insuranceClient?.insuranceCompany;

      // Load and add logo
      await addLogoToPDF(doc);

      // Add company name
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('DARB NATIONAL SERVICE LLC', 20, 38);

      // Add invoice type header
      if (invoiceType === 'insurance') {
        doc.setFontSize(14);
        doc.setTextColor(33, 150, 243);
        doc.text('INSURANCE INVOICE', 105, 20, { align: 'center' });
      }

      // Add invoice details on the right
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
      };
      doc.text(`DATE: ${formatDate(data.invoiceDate)}`, 175, invoiceType === 'insurance' ? 30 : 20, { align: 'right' });
      doc.text(`INVOICE #: ${data.invoiceNumber}`, 175, invoiceType === 'insurance' ? 37 : 27, { align: 'right' });
      doc.text(`Status: ${(data.status || 'draft').toUpperCase()}`, 175, invoiceType === 'insurance' ? 44 : 34, { align: 'right' });

      // Add company details
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let companyY = invoiceType === 'insurance' ? 58 : 48;
      doc.text('CR: 1366462', 20, companyY);
      companyY += 5;
      doc.text('BLOCK 255, STREET, WAY 5508, BUILDING 564', 20, companyY);
      companyY += 5;
      doc.text('VAT NUM: OM1100429932', 20, companyY);
      companyY += 5;
      doc.text('Phone: 80081818 - 24790888', 20, companyY);
      companyY += 5;
      doc.text('Email: INFO@DARBNS.COM', 20, companyY);

      // Add insurance client information
      let billToY = invoiceType === 'insurance' ? 90 : 75;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('BILL TO:', 20, billToY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      let yPos = billToY + 7;
      doc.text(insuranceClient?.customerName || insuranceClient?.companyName || insuranceClient?.contactPerson || '-', 20, yPos);
      
      // Insurance invoice specific fields
      if (invoiceType === 'insurance') {
        if (insuranceCompany?.name) {
          yPos += 5;
          doc.setFont('helvetica', 'bold');
          doc.text(`Insurance Company: ${insuranceCompany.name}`, 20, yPos);
          doc.setFont('helvetica', 'normal');
        }
        if (insuranceClient?.policyNumber) {
          yPos += 5;
          doc.text(`Policy Number: ${insuranceClient.policyNumber}`, 20, yPos);
        }
        if (insuranceClient?.vinNumber) {
          yPos += 5;
          doc.text(`VIN Number: ${insuranceClient.vinNumber}`, 20, yPos);
        }
      }
      
      if (insuranceClient?.email) {
        yPos += 5;
        doc.text(`Email: ${insuranceClient.email}`, 20, yPos);
      }
      if (insuranceClient?.phoneNumber || insuranceClient?.phone) {
        yPos += 5;
        doc.text(`Phone: ${insuranceClient.phoneNumber || insuranceClient.phone}`, 20, yPos);
      }
      if (invoiceType === 'customer' && insuranceClient?.vinNumber) {
        yPos += 5;
        doc.text(`VIN: ${insuranceClient.vinNumber}`, 20, yPos);
      }
      if (insuranceClient?.address) {
        yPos += 5;
        doc.text(`Address: ${insuranceClient.address}`, 20, yPos);
      }

      // Get request data for service information
      const requestData = viewingRequest.attributes || viewingRequest;
      const serviceCode = requestData.serviceCode;
      const serviceNames: { [key: string]: string } = {
        'TOWING_SERVICE': 'Vehicle Towing',
        'BATTERY_BOOST': 'Battery Boost',
        'LOCKOUT_SERVICE': 'Lockout Assistance',
        'FUEL_DELIVERY': 'Fuel Delivery',
      };
      const serviceName = serviceCode ? serviceNames[serviceCode] || serviceCode : '';

      // Add line items table
      const tableData = lineItems.map((item: any) => {
        const attrs = item.attributes || item;
        const product = attrs?.product?.data?.attributes || attrs?.product;
        const description = product?.name || attrs?.description || '-';
        const unitPrice = attrs?.unitPrice || 0;
        
        if (invoiceType === 'insurance') {
          return [
            description,
            serviceName || '-',
            `OMR ${unitPrice.toFixed(2)}`,
            `OMR ${(attrs?.total || 0).toFixed(2)}`,
          ];
        } else {
          return [
            description,
            attrs?.quantity || 0,
            `OMR ${unitPrice.toFixed(2)}`,
            `OMR ${(attrs?.discount || 0).toFixed(2)}`,
            `OMR ${(attrs?.tax || 0).toFixed(2)}`,
            `OMR ${(attrs?.total || 0).toFixed(2)}`,
          ];
        }
      });

      const tableHeaders = invoiceType === 'insurance' 
        ? [['DESCRIPTION', 'SERVICE', 'RATE', 'AMOUNT']]
        : [['Product', 'Qty', 'Unit Price', 'Discount', 'Tax', 'Total']];

      autoTable(doc, {
        startY: yPos + 10,
        head: tableHeaders,
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [33, 150, 243],
          textColor: 255,
          fontStyle: 'bold',
        },
        styles: {
          fontSize: 9,
        },
        columnStyles: invoiceType === 'insurance' 
          ? {
              0: { cellWidth: 60 },
              1: { cellWidth: 40 },
              2: { halign: 'right', cellWidth: 30 },
              3: { halign: 'right', cellWidth: 30 },
            }
          : {
              0: { cellWidth: 60 },
              1: { halign: 'right', cellWidth: 20 },
              2: { halign: 'right', cellWidth: 25 },
              3: { halign: 'right', cellWidth: 25 },
              4: { halign: 'right', cellWidth: 25 },
              5: { halign: 'right', cellWidth: 25 },
            },
      });

      // Add totals
      const finalY = (doc as any).lastAutoTable.finalY || yPos + 10;
      doc.setFontSize(10);
      
      doc.text('Subtotal:', 130, finalY + 10);
      doc.text(`OMR ${(data.subtotal || 0).toFixed(2)}`, 180, finalY + 10, { align: 'right' });
      
      if (invoiceType === 'customer') {
        doc.text('Discount:', 130, finalY + 17);
        doc.text(`- OMR ${(data.discount || 0).toFixed(2)}`, 180, finalY + 17, { align: 'right' });
      }
      
      doc.text('Tax:', 130, finalY + (invoiceType === 'customer' ? 24 : 17));
      doc.text(`OMR ${(data.tax || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 24 : 17), { align: 'right' });
      
      // Total line
      doc.setDrawColor(33, 150, 243);
      doc.setLineWidth(0.5);
      doc.line(130, finalY + (invoiceType === 'customer' ? 27 : 20), 180, finalY + (invoiceType === 'customer' ? 27 : 20));
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Total:', 130, finalY + (invoiceType === 'customer' ? 34 : 27));
      doc.text(`OMR ${(data.total || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 34 : 27), { align: 'right' });

      // Payment info
      if (data.paidAmount > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Paid Amount:', 130, finalY + (invoiceType === 'customer' ? 44 : 37));
        doc.text(`OMR ${(data.paidAmount || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 44 : 37), { align: 'right' });
        
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 0, 0);
        doc.text('Balance Due:', 130, finalY + (invoiceType === 'customer' ? 51 : 44));
        doc.text(`OMR ${(data.balanceDue || 0).toFixed(2)}`, 180, finalY + (invoiceType === 'customer' ? 51 : 44), { align: 'right' });
        doc.setTextColor(0, 0, 0);
      }

      // Add notes if any
      if (data.notes) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', 20, finalY + (invoiceType === 'customer' ? 65 : 58));
        doc.setFont('helvetica', 'normal');
        const splitNotes = doc.splitTextToSize(data.notes, 170);
        doc.text(splitNotes, 20, finalY + (invoiceType === 'customer' ? 72 : 65));
      }

      // Save the PDF
      const fileName = invoiceType === 'insurance' 
        ? `Insurance-Invoice-${data.invoiceNumber}.pdf`
        : `Customer-Invoice-${data.invoiceNumber}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      alert('Failed to download invoice PDF');
    }
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setViewingRequest(null);
  };

  const handleAssignDialogOpen = (request: Request) => {
    setAssigningRequest(request);
    setProviderToAssign(null);
    setAssignDialogOpen(true);
  };

  const handleAssignDialogClose = () => {
    setAssignDialogOpen(false);
    setAssigningRequest(null);
    setProviderToAssign(null);
  };

  const handleAssignProvider = async () => {
    if (!assigningRequest || !providerToAssign) {
      setError('Please select a service provider');
      return;
    }

    try {
      await requestService.assignProvider(assigningRequest.id, providerToAssign.id);
      loadRequests();
      if (detailsOpen && viewingRequest && viewingRequest.id === assigningRequest.id) {
        await reloadRequestDetails(assigningRequest.id);
      }
      handleAssignDialogClose();
    } catch (error: any) {
      console.error('Error assigning provider:', error);
      setError(error.response?.data?.error?.message || 'Failed to assign provider');
    }
  };

  const handleChangeProviderOpen = (request: Request) => {
    setChangingRequest(request);
    setNewProvider(null);
    setChangeProviderOpen(true);
  };

  const handleChangeProviderClose = () => {
    setChangeProviderOpen(false);
    setChangingRequest(null);
    setNewProvider(null);
  };

  const handleChangeProvider = async () => {
    if (!changingRequest || !newProvider) {
      setError('Please select a service provider');
      return;
    }

    const reason = prompt('Please provide reason for changing service provider:');
    if (!reason) return;

    try {
      await requestService.changeProvider(changingRequest.id, newProvider.id, reason);
      loadRequests();
      if (detailsOpen && viewingRequest && viewingRequest.id === changingRequest.id) {
        await reloadRequestDetails(changingRequest.id);
      }
      handleChangeProviderClose();
    } catch (error: any) {
      console.error('Error changing provider:', error);
      setError(error.response?.data?.error?.message || 'Failed to change provider');
    }
  };

  const handleAssignClientOpen = (request: Request) => {
    setAssigningRequestForClient(request);
    setSelectedClientToAssign(null);
    setCreatingNewClient(false);
    setNewClientFormData({
      vinNumber: '',
      customerName: '',
      phoneNumber: '',
      email: '',
      plateNumber: '',
      carMake: '',
      carModel: '',
      carYear: '',
      policyNumber: '',
      policyStartDate: '',
      policyEndDate: '',
      servicePackage: '',
      insuranceCompany: '',
      notes: '',
      isActive: true
    });
    // Extract VIN from request description or notes if available
    const attrs = request.attributes || request;
    const description = attrs.description || '';
    const notes = attrs.notes || '';
    const vinMatch = description.match(/VIN[:\s]*([A-HJ-NPR-Z0-9]{17})/i) || 
                     notes.match(/VIN[:\s]*([A-HJ-NPR-Z0-9]{17})/i);
    if (vinMatch) {
      setNewClientFormData(prev => ({ ...prev, vinNumber: vinMatch[1] }));
    }
    setAssignClientModalOpen(true);
  };

  const handleAssignClientClose = () => {
    setAssignClientModalOpen(false);
    setAssigningRequestForClient(null);
    setSelectedClientToAssign(null);
    setCreatingNewClient(false);
    setNewClientFormData({
      vinNumber: '',
      customerName: '',
      phoneNumber: '',
      email: '',
      plateNumber: '',
      carMake: '',
      carModel: '',
      carYear: '',
      policyNumber: '',
      policyStartDate: '',
      policyEndDate: '',
      servicePackage: '',
      insuranceCompany: '',
      notes: '',
      isActive: true
    });
  };

  const handleCreateAndAssignClient = async () => {
    if (!assigningRequestForClient) return;

    try {
      setAssigningClient(true);
      setError('');

      // Validate required fields
      if (!newClientFormData.vinNumber || !newClientFormData.customerName) {
        setError('VIN Number and Customer Name are required');
        setAssigningClient(false);
        return;
      }

      // Create new insurance client
      const clientData = {
        vinNumber: newClientFormData.vinNumber.trim(),
        customerName: newClientFormData.customerName.trim(),
        phoneNumber: newClientFormData.phoneNumber || null,
        email: newClientFormData.email || null,
        plateNumber: newClientFormData.plateNumber || null,
        carMake: newClientFormData.carMake || null,
        carModel: newClientFormData.carModel || null,
        carYear: newClientFormData.carYear || null,
        policyNumber: newClientFormData.policyNumber || null,
        policyStartDate: newClientFormData.policyStartDate || null,
        policyEndDate: newClientFormData.policyEndDate || null,
        servicePackage: newClientFormData.servicePackage || null,
        insuranceCompany: newClientFormData.insuranceCompany || null,
        notes: newClientFormData.notes || null,
        isActive: newClientFormData.isActive
      };

      const newClient = await insuranceClientService.create(clientData);

      // Clean up notes - remove NOT OUR CLIENT message
      const attrs = assigningRequestForClient.attributes || assigningRequestForClient;
      let cleanedNotes = attrs.notes || '';
      if (cleanedNotes) {
        // Remove the NOT OUR CLIENT message (using [\s\S] instead of . with s flag for compatibility)
        cleanedNotes = cleanedNotes
          .replace(/🔴 NOT OUR CLIENT:[\s\S]*?Sales team must contact customer before assignment\./g, '')
          .replace(/NOT OUR CLIENT:[\s\S]*?Sales team must contact customer before assignment\./g, '')
          .replace(/VIN number was provided but this client is NOT in our database\. This is NOT our client\. Sales team must contact customer before assignment\./g, '')
          .trim();
      }

      // Assign client to request and remove flags
      await requestService.update(assigningRequestForClient.id, {
        insuranceClient: newClient.id || newClient.data?.id,
        isNotOurClient: false,
        notes: cleanedNotes || null
      } as any);

      // Reload requests
      await loadRequests();
      if (detailsOpen && viewingRequest && viewingRequest.id === assigningRequestForClient.id) {
        await reloadRequestDetails(assigningRequestForClient.id);
      }
      
      // Close modal and details
      handleAssignClientClose();
      handleDetailsClose();
      
      setError('');
    } catch (error: any) {
      console.error('Error creating and assigning client:', error);
      setError(error.response?.data?.error?.message || error.message || 'Failed to create and assign client');
    } finally {
      setAssigningClient(false);
    }
  };

  const handleAssignExistingClient = async () => {
    if (!assigningRequestForClient || !selectedClientToAssign) return;

    try {
      setAssigningClient(true);
      setError('');

      const clientId = selectedClientToAssign.id || 
                      selectedClientToAssign.data?.id || 
                      selectedClientToAssign.attributes?.id;

      if (!clientId) {
        setError('Invalid client selected');
        setAssigningClient(false);
        return;
      }

      // Clean up notes - remove NOT OUR CLIENT message
      const attrs = assigningRequestForClient.attributes || assigningRequestForClient;
      let cleanedNotes = attrs.notes || '';
      if (cleanedNotes) {
        // Remove the NOT OUR CLIENT message (using [\s\S] instead of . with s flag for compatibility)
        cleanedNotes = cleanedNotes
          .replace(/🔴 NOT OUR CLIENT:[\s\S]*?Sales team must contact customer before assignment\./g, '')
          .replace(/NOT OUR CLIENT:[\s\S]*?Sales team must contact customer before assignment\./g, '')
          .replace(/VIN number was provided but this client is NOT in our database\. This is NOT our client\. Sales team must contact customer before assignment\./g, '')
          .trim();
      }

      // Assign client to request and remove flags
      await requestService.update(assigningRequestForClient.id, {
        insuranceClient: clientId,
        isNotOurClient: false,
        notes: cleanedNotes || null
      } as any);

      // Reload requests
      await loadRequests();
      if (detailsOpen && viewingRequest && viewingRequest.id === assigningRequestForClient.id) {
        await reloadRequestDetails(assigningRequestForClient.id);
      }
      
      // Close modal and details
      handleAssignClientClose();
      handleDetailsClose();
      
      setError('');
    } catch (error: any) {
      console.error('Error assigning client:', error);
      setError(error.response?.data?.error?.message || error.message || 'Failed to assign client');
    } finally {
      setAssigningClient(false);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const updateData: any = { status: newStatus };
      
      if (newStatus === 'active' && !updateData.assignedAt) {
        // Don't override assignedAt if it already exists
        const request = requests.find(r => r.id === id);
        const attrs = request?.attributes || request;
        if (!attrs?.assignedAt) {
          updateData.assignedAt = new Date().toISOString();
        }
      } else if (newStatus === 'invoice_sent') {
        updateData.invoiceSentAt = new Date().toISOString();
      } else if (newStatus === 'payment_received') {
        updateData.paymentReceivedAt = new Date().toISOString();
      } else if (newStatus === 'completed') {
        updateData.completedDate = new Date().toISOString();
      } else if (newStatus === 'cancelled') {
        const reason = prompt('Please provide a reason for cancellation:');
        if (!reason) return;
        
        // Ask if service limit should be decreased
        const decreaseLimit = window.confirm(
          'Should the client service limit be decreased?\n\n' +
          'Click OK to decrease the service limit (late cancellation)\n' +
          'Click Cancel to NOT decrease the service limit (early cancellation)'
        );
        
        updateData.cancelReason = reason + (decreaseLimit ? ' (service limit decreased)' : ' (service limit NOT decreased)');
        updateData.decreaseServiceLimit = decreaseLimit;
      }
      
      await requestService.update(id, updateData);
      loadRequests();
      if (detailsOpen && viewingRequest && viewingRequest.id === id) {
        await reloadRequestDetails(id);
      }
    } catch (error: any) {
      console.error('Error updating request status:', error);
      setError(error.response?.data?.error?.message || 'Failed to update status');
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Request #',
      'Customer',
      'Phone',
      'Service Provider',
      'Description',
      'Status',
      'Created Date',
      'Scheduled Date',
      'Completed Date',
      'Duration (hours)',
      'Created By'
    ];

    const rows = filteredRequests.map(request => {
      const attrs = request.attributes || request;
      const createdDate = new Date(attrs.createdAt);
      const completedDate = attrs.completedDate ? new Date(attrs.completedDate) : null;
      const duration = completedDate 
        ? Math.round((completedDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60))
        : 'N/A';

      // Get insurance client name
      const insuranceClientId = attrs.insuranceClient?.data?.id || attrs.insuranceClient?.data?.attributes?.id || attrs.insuranceClient?.id || attrs.insuranceClient;
      const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient?.data || attrs.insuranceClient;
      const customerClientName = insuranceClient?.customerName || 'N/A';

      // Get phone number
      const phoneNumber = getRequestPhoneNumber(request) || 'N/A';

      return [
        attrs.requestNumber || `REQ-${request.id}`,
        customerClientName,
        phoneNumber,
        attrs.serviceProvider?.data?.attributes?.name || attrs.serviceProvider?.data?.name || attrs.serviceProvider?.name || 'Not Assigned',
        `"${attrs.description?.replace(/"/g, '""') || ''}"`,
        attrs.status || 'pending',
        createdDate.toLocaleString(),
        attrs.scheduledDate ? new Date(attrs.scheduledDate).toLocaleString() : 'N/A',
        completedDate ? completedDate.toLocaleString() : 'N/A',
        duration,
        attrs.createdByAI ? 'AI' : 'Manual'
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `requests_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      dateFrom: '',
      dateTo: '',
      searchTerm: '',
      serviceProvider: 'all',
      servicePackage: 'all'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'assigned': return 'info';
      case 'active': return 'primary';
      case 'invoice_sent': return 'secondary';
      case 'payment_received': return 'success';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusStep = (status: string) => {
    const steps = ['pending', 'assigned', 'active', 'invoice_sent', 'payment_received', 'completed'];
    return steps.indexOf(status);
  };

  const getStatusStepNumber = (status: string) => {
    const step = getStatusStep(status);
    return step >= 0 ? step + 1 : null;
  };

  const getStatusLabel = (status: string) => {
    const labels: any = {
      'pending': 'Pending',
      'assigned': 'Assigned',
      'active': 'Active',
      'invoice_sent': 'Invoice Sent',
      'payment_received': 'Payment Received',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Update URL so Today/Incomplete tabs are shareable and match Operations Overview link
    const newParams = new URLSearchParams(searchParams);
    if (newValue === 1) newParams.set('tab', 'today');
    else if (newValue === 2) newParams.set('tab', 'incomplete');
    else newParams.delete('tab');
    setSearchParams(newParams, { replace: true });
    // Clear date filters when switching tabs (except when switching to 'All')
    if (newValue !== 0) {
      setFilters(prev => ({ ...prev, dateFrom: '', dateTo: '' }));
    }
  };

  const handlePageChangeWrapper = (event: unknown, newPage: number) => {
    console.log('📄 Page change requested:', newPage, 'Current page:', pagination.page);
    handlePageChange(newPage);
  };

  const handlePageSizeChangeWrapper = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    console.log('📏 Page size change requested:', newSize, 'Current size:', pagination.pageSize);
    handlePageSizeChange(newSize);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Requests</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSortField('createdAt');
              setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
            }}
            startIcon={sortField === 'createdAt' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Date
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSortField('status');
              setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
            }}
            startIcon={sortField === 'status' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Status
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={exportToCSV}
            disabled={filteredRequests.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => setImportModalOpen(true)}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
          >
            Add Request
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="All" />
          <Tab label="Today" />
          <Tab label="Incomplete" />
        </Tabs>
      </Paper>

      {/* Filters Card */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 2 
            }}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Search requests..."
                value={filters.searchTerm}
                onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="assigned">Assigned</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="invoice_sent">Invoice Sent</MenuItem>
                  <MenuItem value="payment_received">Payment Received</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Service Provider</InputLabel>
                <Select
                  value={filters.serviceProvider}
                  label="Service Provider"
                  onChange={(e) => setFilters({ ...filters, serviceProvider: e.target.value })}
                >
                  <MenuItem value="all">All Providers</MenuItem>
                  {serviceProviders.map((provider) => {
                    const attrs = provider.attributes || provider;
                    return (
                      <MenuItem key={provider.id} value={provider.id.toString()}>
                        {attrs.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Date To"
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth>
                <InputLabel>Service Package</InputLabel>
                <Select
                  value={filters.servicePackage}
                  label="Service Package"
                  onChange={(e) => setFilters({ ...filters, servicePackage: e.target.value })}
                >
                  <MenuItem value="all">All Packages</MenuItem>
                  {servicePackages.map((pkg) => {
                    const attrs = pkg.attributes || pkg;
                    return (
                      <MenuItem key={pkg.id} value={pkg.id.toString()}>
                        {attrs.name} ({attrs.code})
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearFilters}
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredRequests.length} of {pagination.total} requests
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Request #</TableCell>
              <TableCell>Client Type</TableCell>
              <TableCell>Customer/Client</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Service Provider</TableCell>
              <TableCell>Service Package</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Completed Date</TableCell>
              <TableCell>Customer Invoice Amount</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.map((request) => {
              const attrs = request.attributes || request;
              // Get insurance client (handle different data structures)
              const ic = attrs.insuranceClient;
              
              // Try to get ID from various structures
              const insuranceClientId = 
                ic?.data?.id || 
                ic?.data?.attributes?.id || 
                ic?.id || 
                ic?.attributes?.id ||
                (typeof ic === 'number' ? ic : null) ||
                (typeof ic === 'string' ? parseInt(ic) : null);
              
              return (
                <TableRow 
                  key={request.id}
                  sx={{
                    backgroundColor: (() => {
                      const notes = attrs.notes || '';
                      const description = attrs.description || '';
                      const isNewUser = attrs.isNewUser || false;
                      const isNotOurClient = attrs.isNotOurClient || false;
                      const requiresPayment = attrs.requiresPayment || false;
                      const isPolicyExpired = attrs.isPolicyExpired || notes.includes('POLICY EXPIRED') || description.includes('POLICY EXPIRED');
                      const isExtraService = notes.includes('EXTRA SERVICE') || description.includes('EXTRA SERVICE') || 
                                           notes.includes('SERVICE LIMIT REACHED') || description.includes('SERVICE LIMIT REACHED');
                      
                      if (isNotOurClient) return '#ffebee';
                      if (isNewUser) return '#ffebee';
                      if (isPolicyExpired) return '#ffebee';
                      if (isExtraService || requiresPayment) return '#fbe9e7';
                      return 'transparent';
                    })(),
                    borderLeft: (() => {
                      const notes = attrs.notes || '';
                      const description = attrs.description || '';
                      const isNewUser = attrs.isNewUser || false;
                      const isNotOurClient = attrs.isNotOurClient || false;
                      const requiresPayment = attrs.requiresPayment || false;
                      const isPolicyExpired = attrs.isPolicyExpired || notes.includes('POLICY EXPIRED') || description.includes('POLICY EXPIRED');
                      const isExtraService = notes.includes('EXTRA SERVICE') || description.includes('EXTRA SERVICE') || 
                                           notes.includes('SERVICE LIMIT REACHED') || description.includes('SERVICE LIMIT REACHED');
                      
                      if (isNotOurClient) return '4px solid #d32f2f';
                      if (isNewUser) return '4px solid #f44336';
                      if (isPolicyExpired) return '4px solid #d32f2f';
                      if (isExtraService || requiresPayment) return '4px solid #ff5722';
                      return 'none';
                    })()
                  }}
                >
                  <TableCell>
                    <Box>
                      {/* Flag Badge */}
                      {(() => {
                        const notes = attrs.notes || '';
                        const description = attrs.description || '';
                        const isNewUser = attrs.isNewUser || false;
                        const isNotOurClient = attrs.isNotOurClient || false;
                        const requiresPayment = attrs.requiresPayment || false;
                        const isPolicyExpired = attrs.isPolicyExpired || notes.includes('POLICY EXPIRED') || description.includes('POLICY EXPIRED');
                        const isExtraService = notes.includes('EXTRA SERVICE') || description.includes('EXTRA SERVICE') || 
                                             notes.includes('SERVICE LIMIT REACHED') || description.includes('SERVICE LIMIT REACHED');
                        
                        let flagLabel = '';
                        let flagColor: 'error' | 'warning' = 'error';
                        
                        if (isNotOurClient) {
                          flagLabel = '🔴 NOT OUR CLIENT';
                          flagColor = 'error';
                        } else if (isNewUser) {
                          flagLabel = '🚩 NEW USER';
                          flagColor = 'error';
                        } else if (isPolicyExpired) {
                          flagLabel = '🔴 POLICY EXPIRED';
                          flagColor = 'error';
                        } else if (isExtraService || requiresPayment) {
                          flagLabel = '🚩 EXTRA SERVICE';
                          flagColor = 'warning';
                        }
                        
                        return flagLabel ? (
                          <Chip 
                            label={flagLabel} 
                            size="small" 
                            color={flagColor}
                            sx={{ 
                              mb: 0.5,
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }} 
                          />
                        ) : null;
                      })()}
                      <Typography variant="body2" fontWeight="bold">
                        {attrs.requestNumber || `REQ-${request.id}`}
                      </Typography>
                      {attrs.createdByAI && (
                        <Chip label="AI" size="small" color="secondary" sx={{ mt: 0.5 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label="Insurance Client" 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // Try multiple data structure patterns
                      let customerName = 
                        ic?.data?.attributes?.customerName ||
                        ic?.data?.customerName ||
                        ic?.attributes?.customerName ||
                        ic?.customerName;
                      
                      // If not found in populated data, try to find in insuranceClients array
                      if (!customerName && insuranceClientId && insuranceClients.length > 0) {
                        const foundClient = insuranceClients.find(client => {
                          const clientId = client.id || client.attributes?.id || client.data?.id || client.data?.attributes?.id;
                          return clientId === insuranceClientId || clientId === Number(insuranceClientId) || Number(clientId) === Number(insuranceClientId);
                        });
                        
                        if (foundClient) {
                          const clientAttrs = foundClient.attributes || foundClient.data?.attributes || foundClient.data || foundClient;
                          customerName = clientAttrs?.customerName;
                        }
                      }
                      
                      return customerName || 'N/A';
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const phoneNumber = getRequestPhoneNumber(request);
                      if (phoneNumber) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {phoneNumber}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openWhatsAppChat(phoneNumber);
                              }}
                              sx={{ 
                                color: 'success.main',
                                '&:hover': { 
                                  backgroundColor: 'success.light',
                                  color: 'success.dark'
                                }
                              }}
                              title="Open WhatsApp"
                            >
                              <WhatsAppIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      }
                      return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                    })()}
                  </TableCell>
                  <TableCell>
                    {attrs.serviceProvider?.data?.attributes?.name || 'Not Assigned'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient?.data || attrs.insuranceClient;
                      const packageName = insuranceClient?.servicePackage?.data?.attributes?.name || 
                                         insuranceClient?.servicePackage?.data?.name ||
                                         insuranceClient?.servicePackage?.attributes?.name ||
                                         insuranceClient?.servicePackage?.name;
                      const packageCode = insuranceClient?.servicePackage?.data?.attributes?.code || 
                                         insuranceClient?.servicePackage?.data?.code ||
                                         insuranceClient?.servicePackage?.attributes?.code ||
                                         insuranceClient?.servicePackage?.code;
                      return packageName ? `${packageName} (${packageCode})` : 'N/A';
                    })()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {(() => {
                        const stepNumber = getStatusStepNumber(attrs.status);
                        const statusLabel = getStatusLabel(attrs.status);
                        const statusColor = getStatusColor(attrs.status);
                        return (
                          <>
                            {stepNumber && (
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 
                                    statusColor === 'success' ? 'success.main' :
                                    statusColor === 'error' ? 'error.main' :
                                    statusColor === 'warning' ? 'warning.main' :
                                    statusColor === 'info' ? 'info.main' :
                                    statusColor === 'primary' ? 'primary.main' :
                                    'grey.300',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {stepNumber}
                              </Box>
                            )}
                            <Chip 
                              label={statusLabel} 
                              color={statusColor} 
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </>
                        );
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(attrs.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {attrs.completedDate 
                        ? new Date(attrs.completedDate).toLocaleDateString()
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const invoices = attrs.invoices?.data || attrs.invoices || [];
                      const customerInvoice = invoices.find((inv: any) => {
                        const invAttrs = inv.attributes || inv;
                        return invAttrs.invoiceType === 'customer' || invAttrs.invoiceType === 'insurance_customer';
                      });
                      
                      if (customerInvoice) {
                        const invAttrs = customerInvoice.attributes || customerInvoice;
                        const total = invAttrs.total || 0;
                        return (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            OMR {parseFloat(total).toFixed(3)}
                          </Typography>
                        );
                      }
                      // Show 0 if no invoice found instead of "-"
                      return (
                        <Typography variant="body2" color="text.secondary">
                          OMR 0.000
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleViewDetails(request)} size="small" title="View Details">
                      <Visibility />
                    </IconButton>
                    {attrs.status === 'pending' && (
                      <IconButton onClick={() => handleOpen(request)} size="small" title="Edit">
                        <Edit />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredRequests.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
                    No requests found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page}
        onPageChange={handlePageChangeWrapper}
        rowsPerPage={pagination.pageSize}
        onRowsPerPageChange={handlePageSizeChangeWrapper}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Add/Edit Request Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRequest ? 'Edit Request' : 'Add Request'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Insurance Client Selection */}
            <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="VIN Number *"
                    value={vinNumber}
                    onChange={(e) => setVinNumber(e.target.value)}
                    placeholder="Enter VIN number..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCheckVin();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCheckVin}
                    disabled={checkingVin || !vinNumber.trim()}
                    sx={{ minWidth: 120 }}
                  >
                    {checkingVin ? 'Checking...' : 'Check VIN'}
                  </Button>
                </Box>

                {/* Package Info Display */}
                {clientPackageInfo && selectedInsuranceClient && (
                  <Card variant="outlined" sx={{ mb: 2, bgcolor: 'background.default' }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Client Information
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Name:</strong> {selectedInsuranceClient.customerName}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>VIN:</strong> {selectedInsuranceClient.vinNumber}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Vehicle:</strong> {selectedInsuranceClient.carMake} {selectedInsuranceClient.carModel} ({selectedInsuranceClient.plateNumber})
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Insurance Company:</strong> {selectedInsuranceClient.insuranceCompany?.data?.attributes?.name || selectedInsuranceClient.insuranceCompany?.attributes?.name || selectedInsuranceClient.insuranceCompany?.name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Package:</strong> {selectedInsuranceClient.servicePackage?.name || selectedInsuranceClient.servicePackage?.code}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Policy Status:</strong>{' '}
                        <Chip
                          label={clientPackageInfo.policy?.isActive ? 'Active' : 'Expired'}
                          color={clientPackageInfo.policy?.isActive ? 'success' : 'error'}
                          size="small"
                        />
                        {clientPackageInfo.policy?.daysRemaining !== null && (
                          <span> ({clientPackageInfo.policy.daysRemaining} days remaining)</span>
                        )}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Available Services
                      </Typography>
                      {clientPackageInfo.serviceUsage?.sharedLimitInfo && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                          <Typography variant="caption">
                            <strong>Shared Limit:</strong> Battery Boost, Lockout Assistance, and Fuel Delivery share 1 total usage.
                            Remaining: {clientPackageInfo.serviceUsage.sharedLimitInfo.remaining}
                          </Typography>
                        </Alert>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {clientPackageInfo.serviceUsage?.services?.map((service: any, idx: number) => (
                          <Chip
                            key={idx}
                            label={`${service.serviceName}: ${service.remainingCount} remaining`}
                            color={service.canUseAgain ? 'success' : 'error'}
                            size="small"
                            variant={service.isSharedLimit ? 'outlined' : 'filled'}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}
            </Box>

            {/* Service Selection - Show for insurance clients with active policy/status and service package */}
            {selectedInsuranceClient && 
             clientPackageInfo && 
             clientPackageInfo.client?.servicePackage &&
             clientPackageInfo.policy?.isActive !== false && 
             clientPackageInfo.isActive && (
              <FormControl fullWidth required>
                <InputLabel>Service Type *</InputLabel>
                <Select
                  value={formData.serviceCode}
                  label="Service Type *"
                  onChange={(e) => {
                    const serviceCode = e.target.value;
                    const service = clientPackageInfo.serviceUsage?.services?.find(
                      (s: any) => s.serviceCode === serviceCode
                    );
                    setFormData({
                      ...formData,
                      serviceCode: serviceCode,
                      serviceType: service?.serviceName || ''
                    });
                  }}
                  required
                >
                  <MenuItem value="">
                    <em>Select a service</em>
                  </MenuItem>
                  {clientPackageInfo.serviceUsage?.services
                    ?.filter((service: any) => service.canUseAgain && service.remainingCount > 0)
                    .map((service: any) => (
                      <MenuItem key={service.serviceCode} value={service.serviceCode}>
                        {service.serviceName} ({service.remainingCount} remaining)
                        {service.isSharedLimit && ' [Shared Limit]'}
                      </MenuItem>
                    ))}
                </Select>
                {clientPackageInfo.serviceUsage?.services?.filter((s: any) => s.canUseAgain && s.remainingCount > 0).length === 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        No services available. All service limits have been reached.
                      </Typography>
                    </Alert>
                    
                    {/* Show summary of requests that used services */}
                    {clientPackageInfo.requestHistory?.serviceRequests && 
                     clientPackageInfo.requestHistory.serviceRequests.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Service Usage History:
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Request #</strong></TableCell>
                                <TableCell><strong>Service</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>Provider</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {clientPackageInfo.requestHistory.serviceRequests.map((req: any) => (
                                <TableRow key={req.id}>
                                  <TableCell>
                                    {req.requestNumber || `#${req.id}`}
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={req.serviceType || req.serviceCode || 'N/A'} 
                                      size="small" 
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={req.status || 'N/A'} 
                                      size="small"
                                      color={
                                        req.status === 'completed' ? 'success' :
                                        req.status === 'cancelled' ? 'error' :
                                        req.status === 'pending' ? 'warning' :
                                        'default'
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {req.completedDate 
                                      ? new Date(req.completedDate).toLocaleDateString()
                                      : req.createdAt 
                                        ? new Date(req.createdAt).toLocaleDateString()
                                        : 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {req.serviceProvider || '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Total service requests: {clientPackageInfo.requestHistory.serviceRequests.length}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                {formData.serviceCode && (
                  <Box sx={{ mt: 1 }}>
                    {clientPackageInfo.serviceUsage?.services?.find(
                      (s: any) => s.serviceCode === formData.serviceCode
                    )?.isSharedLimit && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Shared Limit:</strong> This service shares a limit with Battery Boost, Lockout Assistance, and Fuel Delivery. 
                          Using this service will make all three services unavailable.
                        </Typography>
                      </Alert>
                    )}
                    {clientPackageInfo.serviceUsage?.services?.find(
                      (s: any) => s.serviceCode === formData.serviceCode
                    ) && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Used: {clientPackageInfo.serviceUsage?.services?.find(
                          (s: any) => s.serviceCode === formData.serviceCode
                        )?.usedCount || 0} / Allowed: {clientPackageInfo.serviceUsage?.services?.find(
                          (s: any) => s.serviceCode === formData.serviceCode
                        )?.allowedCount || 0}
                      </Typography>
                    )}
                  </Box>
                )}
              </FormControl>
            )}
            
            {/* Service Selection - Only show for insurance clients with active policy and service package */}
            {selectedInsuranceClient && 
             clientPackageInfo && 
             clientPackageInfo.client?.servicePackage &&
             clientPackageInfo.policy?.isActive && (
              <FormControl fullWidth required>
                <InputLabel>Service Type *</InputLabel>
                <Select
                  value={formData.serviceCode}
                  label="Service Type *"
                  onChange={(e) => {
                    const serviceCode = e.target.value;
                    const service = clientPackageInfo.serviceUsage?.services?.find(
                      (s: any) => s.serviceCode === serviceCode
                    );
                    setFormData({
                      ...formData,
                      serviceCode: serviceCode,
                      serviceType: service?.serviceName || ''
                    });
                  }}
                  required
                >
                  <MenuItem value="">
                    <em>Select a service</em>
                  </MenuItem>
                  {clientPackageInfo.serviceUsage?.services
                    ?.filter((service: any) => service.canUseAgain && service.remainingCount > 0)
                    .map((service: any) => (
                      <MenuItem key={service.serviceCode} value={service.serviceCode}>
                        {service.serviceName} ({service.remainingCount} remaining)
                        {service.isSharedLimit && ' [Shared Limit]'}
                      </MenuItem>
                    ))}
                </Select>
                {clientPackageInfo.serviceUsage?.services?.filter((s: any) => s.canUseAgain && s.remainingCount > 0).length === 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        No services available. All service limits have been reached.
                      </Typography>
                    </Alert>
                    
                    {/* Show summary of requests that used services */}
                    {clientPackageInfo.requestHistory?.serviceRequests && 
                     clientPackageInfo.requestHistory.serviceRequests.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Service Usage History:
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Request #</strong></TableCell>
                                <TableCell><strong>Service</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Date</strong></TableCell>
                                <TableCell><strong>Provider</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {clientPackageInfo.requestHistory.serviceRequests.map((req: any) => (
                                <TableRow key={req.id}>
                                  <TableCell>
                                    {req.requestNumber || `#${req.id}`}
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={req.serviceType || req.serviceCode || 'N/A'} 
                                      size="small" 
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={req.status || 'N/A'} 
                                      size="small"
                                      color={
                                        req.status === 'completed' ? 'success' :
                                        req.status === 'cancelled' ? 'error' :
                                        req.status === 'pending' ? 'warning' :
                                        'default'
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {req.completedDate 
                                      ? new Date(req.completedDate).toLocaleDateString()
                                      : req.createdAt 
                                        ? new Date(req.createdAt).toLocaleDateString()
                                        : 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {req.serviceProvider || '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Total service requests: {clientPackageInfo.requestHistory.serviceRequests.length}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                {formData.serviceCode && (
                  <Box sx={{ mt: 1 }}>
                    {clientPackageInfo.serviceUsage?.services?.find(
                      (s: any) => s.serviceCode === formData.serviceCode
                    )?.isSharedLimit && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="caption">
                          <strong>Shared Limit:</strong> This service shares a limit with Battery Boost, Lockout Assistance, and Fuel Delivery. 
                          Using this service will make all three services unavailable.
                        </Typography>
                      </Alert>
                    )}
                    {clientPackageInfo.serviceUsage?.services?.find(
                      (s: any) => s.serviceCode === formData.serviceCode
                    ) && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Used: {clientPackageInfo.serviceUsage?.services?.find(
                          (s: any) => s.serviceCode === formData.serviceCode
                        )?.usedCount || 0} / Allowed: {clientPackageInfo.serviceUsage?.services?.find(
                          (s: any) => s.serviceCode === formData.serviceCode
                        )?.allowedCount || 0}
                      </Typography>
                    )}
                  </Box>
                )}
              </FormControl>
            )}
            
            {/* Show message if policy is expired */}
            {selectedInsuranceClient && 
             clientPackageInfo && 
             !clientPackageInfo.policy?.isActive && (
              <Alert severity="error">
                This client's policy has expired or is inactive. Cannot create service requests.
              </Alert>
            )}
            
            {/* Service Provider Selection */}
            <Autocomplete
              fullWidth
              options={serviceProviders}
              value={selectedProvider}
              onChange={(_, newValue) => {
                setSelectedProvider(newValue);
                setFormData({ ...formData, serviceProvider: newValue?.id || '' });
              }}
              getOptionLabel={(option) => {
                const attrs = option.attributes || option;
                return attrs.name || `Provider ${option.id}`;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Service Provider"
                  placeholder="Search provider..."
                />
              )}
              renderOption={(props, option) => {
                const attrs = option.attributes || option;
                return (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">{attrs.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {attrs.type} - {attrs.phone}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No service providers found"
            />
            <TextField
              fullWidth
              label="Description *"
              multiline
              rows={4}
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              error={!formData.description && error !== ''}
            />
            <TextField
              fullWidth
              label="Phone Number *"
              required
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
              error={!formData.phoneNumber && error !== ''}
              placeholder="Enter phone number with country code (e.g., +96812345678)"
              helperText="Phone number from which the request was sent"
            />
            <TextField
              fullWidth
              label="Scheduled Date"
              type="date"
              value={formData.scheduledDate}
              onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
            
            {/* Before Photo Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Before Photo
              </Typography>
              {beforePhotoPreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                  <img 
                    src={beforePhotoPreview} 
                    alt="Before" 
                    style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                  />
                  <IconButton
                    size="small"
                    onClick={removeBeforePhoto}
                    sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ) : null}
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="before-photo-upload"
                type="file"
                onChange={handleBeforePhotoChange}
              />
              <label htmlFor="before-photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  {beforePhotoPreview ? 'Change Before Photo' : 'Upload Before Photo'}
                </Button>
              </label>
            </Box>

            {/* After Photo Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                After Photo
              </Typography>
              {afterPhotoPreview ? (
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                  <img 
                    src={afterPhotoPreview} 
                    alt="After" 
                    style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                  />
                  <IconButton
                    size="small"
                    onClick={removeAfterPhoto}
                    sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ) : null}
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="after-photo-upload"
                type="file"
                onChange={handleAfterPhotoChange}
              />
              <label htmlFor="after-photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                >
                  {afterPhotoPreview ? 'Change After Photo' : 'Upload After Photo'}
                </Button>
              </label>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={uploading}>
            {uploading ? 'Uploading...' : (editingRequest ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog - Improved Design */}
      <Dialog open={detailsOpen} onClose={handleDetailsClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" fontWeight="bold">Request Details</Typography>
              {viewingRequest && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Request #{viewingRequest.requestNumber || viewingRequest.attributes?.requestNumber || `REQ-${viewingRequest.id}`}
                </Typography>
              )}
            </Box>
            {viewingRequest && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => {
                    const req = viewingRequest as any;
                    handleDetailsClose();
                    handleOpen(req);
                  }}
                >
                  Edit
                </Button>
                {(() => {
                  const stepNumber = getStatusStepNumber(viewingRequest.status || viewingRequest.attributes?.status || 'pending');
                  const statusColor = getStatusColor(viewingRequest.status || viewingRequest.attributes?.status || 'pending');
                  const isImported = viewingRequest.imported || viewingRequest.attributes?.imported;
                  return (
                    <>
                      {stepNumber && (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 
                              statusColor === 'success' ? 'success.main' :
                              statusColor === 'error' ? 'error.main' :
                              statusColor === 'warning' ? 'warning.main' :
                              statusColor === 'info' ? 'info.main' :
                              statusColor === 'primary' ? 'primary.main' :
                              'grey.300',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem'
                          }}
                        >
                          {stepNumber}
                        </Box>
                      )}
                      <Chip 
                        label={getStatusLabel(viewingRequest.status || viewingRequest.attributes?.status || 'pending')} 
                        color={statusColor} 
                        size="medium"
                        sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                      />
                      {isImported && (
                        <Chip 
                          label="Imported" 
                          color="default" 
                          size="small"
                          sx={{ 
                            fontWeight: 500, 
                            fontSize: '0.75rem',
                            bgcolor: 'grey.200',
                            color: 'grey.700'
                          }}
                        />
                      )}
                    </>
                  );
                })()}
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewingRequest && (
            <Stack spacing={3}>
              {/* Workflow Stepper */}
              <Card variant="outlined" sx={{ p: 3, bgcolor: 'background.default' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Workflow Progress
                </Typography>
                <Stepper 
                  activeStep={getStatusStep(viewingRequest.attributes?.status || viewingRequest.status || 'pending')} 
                  alternativeLabel
                  sx={{ mb: 2 }}
                >
                  <Step><StepLabel>Pending</StepLabel></Step>
                  <Step><StepLabel>Assigned</StepLabel></Step>
                  <Step><StepLabel>Active</StepLabel></Step>
                  <Step><StepLabel>Invoice Sent</StepLabel></Step>
                  <Step><StepLabel>Payment Received</StepLabel></Step>
                  <Step><StepLabel>Completed</StepLabel></Step>
                </Stepper>
                <Alert 
                  severity={
                    (viewingRequest.status === 'completed' || viewingRequest.attributes?.status === 'completed') ? 'success' :
                    (viewingRequest.status === 'cancelled' || viewingRequest.attributes?.status === 'cancelled') ? 'error' :
                    (viewingRequest.status === 'pending' || viewingRequest.attributes?.status === 'pending') ? 'info' : 'success'
                  } 
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Current Status:</strong> {getStatusLabel(viewingRequest.status || viewingRequest.attributes?.status || 'pending')}
                  </Typography>
                </Alert>
              </Card>

              {/* Request Information */}
              <Card variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Request Information
                </Typography>
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip 
                          label={getStatusLabel(viewingRequest.status || viewingRequest.attributes?.status || 'pending')} 
                          color={getStatusColor(viewingRequest.status || viewingRequest.attributes?.status || 'pending')} 
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Created By
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {(viewingRequest.createdByAI || viewingRequest.attributes?.createdByAI) ? '🤖 AI Generated' : '👤 Manual Entry'}
                      </Typography>
                    </Box>
                  </Box>
                  {(viewingRequest.phoneNumber || viewingRequest.attributes?.phoneNumber) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Request Phone Number
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        📱 {viewingRequest.phoneNumber || viewingRequest.attributes?.phoneNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                        Phone number from which this request was created (may differ from client number)
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                      Reason (user requested service)
                    </Typography>
                    <Card variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body1" component="span" sx={{ whiteSpace: 'pre-wrap' }}>
                        {descriptionWithMapLink(viewingRequest.description || viewingRequest.attributes?.description) ?? '—'}
                      </Typography>
                    </Card>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                      Reason (client breakdown)
                    </Typography>
                    <Card variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {(() => {
                          const reasons = getReasonsFromRequestEntity(viewingRequest);
                          return reasons.length > 0 ? reasons.join(', ') : '—';
                        })()}
                      </Typography>
                    </Card>
                  </Box>
                  {(viewingRequest.scheduledDate || viewingRequest.attributes?.scheduledDate) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Scheduled Date
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        📅 {new Date(viewingRequest.scheduledDate || viewingRequest.attributes?.scheduledDate).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {(viewingRequest.notes || viewingRequest.attributes?.notes) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Additional Notes
                      </Typography>
                      <Card variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {viewingRequest.notes || viewingRequest.attributes?.notes}
                        </Typography>
                      </Card>
                    </Box>
                  )}
                  
                  {/* Cancel Reason - Show if request is cancelled */}
                  {(viewingRequest.status === 'cancelled' || viewingRequest.attributes?.status === 'cancelled') && 
                   (viewingRequest.cancelReason || viewingRequest.attributes?.cancelReason) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        ❌ Cancellation Reason
                      </Typography>
                      <Card variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'error.50', borderColor: 'error.main', borderWidth: 2 }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'error.dark', fontWeight: 500 }}>
                          {viewingRequest.cancelReason || viewingRequest.attributes?.cancelReason}
                        </Typography>
                      </Card>
                    </Box>
                  )}
                  
                  {/* Provider Change Reason - Show if provider was changed */}
                  {(viewingRequest.providerChangeReason || viewingRequest.attributes?.providerChangeReason) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        🔄 Provider Change Reason
                      </Typography>
                      <Card variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'warning.50', borderColor: 'warning.main', borderWidth: 2 }}>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'warning.dark', fontWeight: 500 }}>
                          {viewingRequest.providerChangeReason || viewingRequest.attributes?.providerChangeReason}
                        </Typography>
                        {(viewingRequest.previousServiceProvider || viewingRequest.attributes?.previousServiceProvider) && (
                          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                            Previous Provider: {
                              viewingRequest.previousServiceProvider?.data?.attributes?.name || 
                              viewingRequest.attributes?.previousServiceProvider?.data?.attributes?.name ||
                              viewingRequest.previousServiceProvider?.name ||
                              viewingRequest.attributes?.previousServiceProvider?.name ||
                              'N/A'
                            }
                          </Typography>
                        )}
                      </Card>
                    </Box>
                  )}
                  
                  {/* Before/After Photos */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem', mb: 1, display: 'block' }}>
                      Evidence Photos
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Before
                        </Typography>
                        {(() => {
                          const attrs = viewingRequest.attributes || viewingRequest;
                          const href = getRequestLocationHref(attrs);
                          return href ? (
                            <Link
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ display: 'block', mb: 0.75, fontSize: '0.75rem' }}
                            >
                              Location link
                            </Link>
                          ) : null;
                        })()}
                        {(() => {
                          const beforePhoto = viewingRequest.attributes?.evidencePhotoBefore?.data || viewingRequest.attributes?.evidencePhotoBefore;
                          const imageUrl = getImageUrl(beforePhoto);
                          const hasImage = hasValidImage(beforePhoto) && !imageErrors.before && imageUrl;
                          
                          return hasImage ? (
                            <Box>
                              <img
                                src={imageUrl}
                                alt="Before"
                                style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                                onError={() => setImageErrors(prev => ({ ...prev, before: true }))}
                              />
                              <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="admin-before-photo-upload-request"
                                type="file"
                                onChange={handleAdminBeforePhotoUpload}
                                disabled={uploading}
                              />
                              <label htmlFor="admin-before-photo-upload-request">
                                <Button
                                  variant="outlined"
                                  component="span"
                                  size="small"
                                  startIcon={<CloudUpload />}
                                  sx={{ mt: 1, width: '100%' }}
                                  disabled={uploading}
                                >
                                  {uploading ? 'Uploading...' : 'Upload/Change'}
                                </Button>
                              </label>
                            </Box>
                          ) : (
                            <Box>
                              <Box
                                sx={{
                                  width: '300px',
                                  height: '300px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  backgroundColor: '#f5f5f5',
                                  color: 'text.secondary'
                                }}
                              >
                                <Typography variant="body2">No image</Typography>
                              </Box>
                              <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="admin-before-photo-upload-request"
                                type="file"
                                onChange={handleAdminBeforePhotoUpload}
                                disabled={uploading}
                              />
                              <label htmlFor="admin-before-photo-upload-request">
                                <Button
                                  variant="outlined"
                                  component="span"
                                  size="small"
                                  startIcon={<CloudUpload />}
                                  sx={{ mt: 1, width: '100%' }}
                                  disabled={uploading}
                                >
                                  {uploading ? 'Uploading...' : 'Upload Photo'}
                                </Button>
                              </label>
                            </Box>
                          );
                        })()}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          After
                        </Typography>
                        <Autocomplete
                          size="small"
                          options={TO_DROP_DOWN_OPTIONS}
                          value={(() => {
                            const attrs = viewingRequest.attributes || viewingRequest;
                            const current = attrs?.toDropDown ?? null;
                            return current && TO_DROP_DOWN_OPTIONS.includes(current) ? current : null;
                          })()}
                          onChange={async (_e, newValue) => {
                            if (!viewingRequest) return;
                            try {
                              await requestService.update(viewingRequest.id, { toDropDown: newValue || null } as any);
                              setViewingRequest((prev) => {
                                if (!prev) return prev;
                                const prevAttrs = prev.attributes || {};
                                return { ...prev, attributes: { ...prevAttrs, toDropDown: newValue || null } };
                              });
                              await loadRequests();
                            } catch (err: any) {
                              console.error('Failed to update toDropDown:', err);
                              setError(err?.response?.data?.error?.message || 'Failed to update To Drop Down');
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="To Drop Down"
                              placeholder="Select..."
                              sx={{ mb: 1, minWidth: 300 }}
                            />
                          )}
                        />
                        {(() => {
                          const afterPhoto = viewingRequest.attributes?.evidencePhotoAfter?.data || viewingRequest.attributes?.evidencePhotoAfter;
                          const imageUrl = getImageUrl(afterPhoto);
                          const hasImage = hasValidImage(afterPhoto) && !imageErrors.after && imageUrl;
                          
                          return hasImage ? (
                            <Box>
                              <img
                                src={imageUrl}
                                alt="After"
                                style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '4px', border: '1px solid #ddd' }}
                                onError={() => setImageErrors(prev => ({ ...prev, after: true }))}
                              />
                              <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="admin-after-photo-upload-request"
                                type="file"
                                onChange={handleAdminAfterPhotoUpload}
                                disabled={uploading}
                              />
                              <label htmlFor="admin-after-photo-upload-request">
                                <Button
                                  variant="outlined"
                                  component="span"
                                  size="small"
                                  startIcon={<CloudUpload />}
                                  sx={{ mt: 1, width: '100%' }}
                                  disabled={uploading}
                                >
                                  {uploading ? 'Uploading...' : 'Upload/Change'}
                                </Button>
                              </label>
                            </Box>
                          ) : (
                            <Box>
                              <Box
                                sx={{
                                  width: '300px',
                                  height: '300px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  backgroundColor: '#f5f5f5',
                                  color: 'text.secondary'
                                }}
                              >
                                <Typography variant="body2">No image</Typography>
                              </Box>
                              <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="admin-after-photo-upload-request"
                                type="file"
                                onChange={handleAdminAfterPhotoUpload}
                                disabled={uploading}
                              />
                              <label htmlFor="admin-after-photo-upload-request">
                                <Button
                                  variant="outlined"
                                  component="span"
                                  size="small"
                                  startIcon={<CloudUpload />}
                                  sx={{ mt: 1, width: '100%' }}
                                  disabled={uploading}
                                >
                                  {uploading ? 'Uploading...' : 'Upload Photo'}
                                </Button>
                              </label>
                            </Box>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Card>

              {/* Customer/Insurance Client Details */}
              <Card variant="outlined" sx={{ p: 3 }}>
                {viewingRequest.insuranceClient?.data?.attributes || viewingRequest.attributes?.insuranceClient?.data?.attributes ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <Typography variant="h6" fontWeight="bold">Client Information</Typography>
                      <Chip label="Insurance Client" size="small" color="primary" variant="outlined" />
                    </Box>
                    <Stack spacing={2.5}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                            Customer Name
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                            👤 {viewingRequest.insuranceClient?.data?.attributes?.customerName || viewingRequest.attributes?.insuranceClient?.data?.attributes?.customerName ? (
                              <Link
                                component="button"
                                variant="body1"
                                onClick={() => {
                                  const clientId = viewingRequest.insuranceClient?.data?.id || viewingRequest.attributes?.insuranceClient?.data?.id;
                                  if (clientId) {
                                    navigate(`/insurance-clients/${clientId}/overview`);
                                  }
                                }}
                                sx={{ 
                                  fontWeight: 500, 
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {viewingRequest.insuranceClient?.data?.attributes?.customerName || viewingRequest.attributes?.insuranceClient?.data?.attributes?.customerName}
                              </Link>
                            ) : 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                            VIN Number
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500, fontFamily: 'monospace' }}>
                            🚗 {viewingRequest.insuranceClient?.data?.attributes?.vinNumber || viewingRequest.attributes?.insuranceClient?.data?.attributes?.vinNumber || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                            Vehicle
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                            🚙 {viewingRequest.insuranceClient?.data?.attributes?.carMake || viewingRequest.attributes?.insuranceClient?.data?.attributes?.carMake || ''} {viewingRequest.insuranceClient?.data?.attributes?.carModel || viewingRequest.attributes?.insuranceClient?.data?.attributes?.carModel || ''} ({viewingRequest.insuranceClient?.data?.attributes?.plateNumber || viewingRequest.attributes?.insuranceClient?.data?.attributes?.plateNumber || 'N/A'})
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                            Insurance Company
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                            🏢 {viewingRequest.insuranceClient?.data?.attributes?.insuranceCompany?.data?.attributes?.name || viewingRequest.attributes?.insuranceClient?.data?.attributes?.insuranceCompany?.data?.attributes?.name || viewingRequest.insuranceClient?.data?.attributes?.insuranceCompany?.name || viewingRequest.attributes?.insuranceClient?.data?.attributes?.insuranceCompany?.name || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                            Phone
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                              📞 {viewingRequest.insuranceClient?.data?.attributes?.phoneNumber || viewingRequest.attributes?.insuranceClient?.data?.attributes?.phoneNumber || 'N/A'}
                            </Typography>
                            {(viewingRequest.insuranceClient?.data?.attributes?.phoneNumber || viewingRequest.attributes?.insuranceClient?.data?.attributes?.phoneNumber) && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const phone = viewingRequest.insuranceClient?.data?.attributes?.phoneNumber || viewingRequest.attributes?.insuranceClient?.data?.attributes?.phoneNumber;
                                  if (phone) {
                                    openWhatsAppChat(phone);
                                  }
                                }}
                                sx={{ 
                                  color: 'success.main',
                                  '&:hover': { 
                                    backgroundColor: 'success.light',
                                    color: 'success.dark'
                                  }
                                }}
                                title="Open WhatsApp"
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                            Email
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                            ✉️ {viewingRequest.insuranceClient?.data?.attributes?.email || viewingRequest.attributes?.insuranceClient?.data?.attributes?.email || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      {(viewingRequest.insuranceClient?.data?.attributes?.servicePackage || viewingRequest.attributes?.insuranceClient?.data?.attributes?.servicePackage) && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                            Service Package
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              label={`${viewingRequest.insuranceClient?.data?.attributes?.servicePackage?.data?.attributes?.name || viewingRequest.attributes?.insuranceClient?.data?.attributes?.servicePackage?.data?.attributes?.name || viewingRequest.insuranceClient?.data?.attributes?.servicePackage?.name || viewingRequest.attributes?.insuranceClient?.data?.attributes?.servicePackage?.name} (${viewingRequest.insuranceClient?.data?.attributes?.servicePackage?.data?.attributes?.code || viewingRequest.attributes?.insuranceClient?.data?.attributes?.servicePackage?.data?.attributes?.code || viewingRequest.insuranceClient?.data?.attributes?.servicePackage?.code || viewingRequest.attributes?.insuranceClient?.data?.attributes?.servicePackage?.code})`}
                              size="small" 
                              color="primary"
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>
                        </Box>
                      )}
                      {(viewingRequest.serviceType || viewingRequest.attributes?.serviceType || viewingRequest.serviceCode || viewingRequest.attributes?.serviceCode) && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                            Service Requested
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip 
                              label={viewingRequest.serviceType || viewingRequest.attributes?.serviceType || viewingRequest.serviceCode || viewingRequest.attributes?.serviceCode || 'N/A'} 
                              size="small" 
                              color="secondary"
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </>
                ) : viewingRequest.customer?.data?.attributes || viewingRequest.attributes?.customer?.data?.attributes ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">Customer Information</Typography>
                      <Chip label="Regular Customer" size="small" color="default" variant="outlined" />
                    </Box>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">Contact Person</Typography>
                          <Typography variant="body1">
                            👤 {viewingRequest.customer?.data?.attributes?.contactPerson || viewingRequest.attributes?.customer?.data?.attributes?.contactPerson ? (
                              <Link
                                component="button"
                                variant="body1"
                                onClick={() => {
                                  const customerId = viewingRequest.customer?.data?.id || viewingRequest.attributes?.customer?.data?.id;
                                  if (customerId) {
                                    navigate(`/contacts/${customerId}`);
                                  }
                                }}
                                sx={{ 
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {viewingRequest.customer?.data?.attributes?.contactPerson || viewingRequest.attributes?.customer?.data?.attributes?.contactPerson}
                              </Link>
                            ) : 'N/A'}
                          </Typography>
                        </Box>
                        {(viewingRequest.customer?.data?.attributes?.companyName || viewingRequest.attributes?.customer?.data?.attributes?.companyName) && (
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Company Name</Typography>
                            <Typography variant="body1">
                              🏢 {viewingRequest.customer?.data?.attributes?.companyName || viewingRequest.attributes?.customer?.data?.attributes?.companyName}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                          <Typography variant="body1">
                            ✉️ {viewingRequest.customer?.data?.attributes?.email || viewingRequest.attributes?.customer?.data?.attributes?.email || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      {(viewingRequest.customer?.data?.attributes?.vinNumber || viewingRequest.attributes?.customer?.data?.attributes?.vinNumber) && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">VIN Number</Typography>
                            <Typography variant="body1">
                              🚗 {viewingRequest.customer?.data?.attributes?.vinNumber || viewingRequest.attributes?.customer?.data?.attributes?.vinNumber || 'N/A'}
                            </Typography>
                          </Box>
                          {(viewingRequest.customer?.data?.attributes?.plateNumber || viewingRequest.attributes?.customer?.data?.attributes?.plateNumber) && (
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" color="text.secondary">Vehicle</Typography>
                              <Typography variant="body1">
                                🚙 {viewingRequest.customer?.data?.attributes?.carMake || viewingRequest.attributes?.customer?.data?.attributes?.carMake || ''} {viewingRequest.customer?.data?.attributes?.carModel || viewingRequest.attributes?.customer?.data?.attributes?.carModel || ''} ({viewingRequest.customer?.data?.attributes?.plateNumber || viewingRequest.attributes?.customer?.data?.attributes?.plateNumber || 'N/A'})
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                              📞 {viewingRequest.customer?.data?.attributes?.phone || viewingRequest.attributes?.customer?.data?.attributes?.phone || 'N/A'}
                            </Typography>
                            {(viewingRequest.customer?.data?.attributes?.phone || viewingRequest.attributes?.customer?.data?.attributes?.phone) && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const phone = viewingRequest.customer?.data?.attributes?.phone || viewingRequest.attributes?.customer?.data?.attributes?.phone;
                                  if (phone) {
                                    openWhatsAppChat(phone);
                                  }
                                }}
                                sx={{ 
                                  color: 'success.main',
                                  '&:hover': { 
                                    backgroundColor: 'success.light',
                                    color: 'success.dark'
                                  }
                                }}
                                title="Open WhatsApp"
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                          <Typography variant="body1">
                            📍 {viewingRequest.customer?.data?.attributes?.address || viewingRequest.attributes?.customer?.data?.attributes?.address || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      {(viewingRequest.customer?.data?.attributes?.servicePackage || viewingRequest.attributes?.customer?.data?.attributes?.servicePackage) && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Service Package</Typography>
                          <Typography variant="body1">
                            📦 {viewingRequest.customer?.data?.attributes?.servicePackage?.data?.attributes?.name || viewingRequest.attributes?.customer?.data?.attributes?.servicePackage?.data?.attributes?.name || viewingRequest.customer?.data?.attributes?.servicePackage?.name || viewingRequest.attributes?.customer?.data?.attributes?.servicePackage?.name} ({viewingRequest.customer?.data?.attributes?.servicePackage?.data?.attributes?.code || viewingRequest.attributes?.customer?.data?.attributes?.servicePackage?.data?.attributes?.code || viewingRequest.customer?.data?.attributes?.servicePackage?.code || viewingRequest.attributes?.customer?.data?.attributes?.servicePackage?.code})
                          </Typography>
                        </Box>
                      )}
                      {(viewingRequest.serviceType || viewingRequest.attributes?.serviceType || viewingRequest.serviceCode || viewingRequest.attributes?.serviceCode) && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">Service Requested</Typography>
                          <Chip 
                            label={viewingRequest.serviceType || viewingRequest.attributes?.serviceType || viewingRequest.serviceCode || viewingRequest.attributes?.serviceCode || 'N/A'} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </Stack>
                  </>
                ) : (
                  <Alert severity="warning">No client information available</Alert>
                )}
              </Card>

              {/* Service Provider Details */}
              <Card variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Service Provider
                </Typography>
                {viewingRequest.serviceProvider?.data?.attributes || viewingRequest.attributes?.serviceProvider?.data?.attributes ? (
                  <Stack spacing={2.5}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                          Provider Name
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                          🏢 {viewingRequest.serviceProvider?.data?.attributes?.name || viewingRequest.attributes?.serviceProvider?.data?.attributes?.name ? (
                            <Link
                              component="button"
                              variant="body1"
                              onClick={() => {
                                const providerId = viewingRequest.serviceProvider?.data?.id || viewingRequest.attributes?.serviceProvider?.data?.id;
                                if (providerId) {
                                  navigate(`/service-providers/${providerId}/overview`);
                                }
                              }}
                              sx={{ 
                                fontWeight: 500, 
                                textDecoration: 'none',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {viewingRequest.serviceProvider?.data?.attributes?.name || viewingRequest.attributes?.serviceProvider?.data?.attributes?.name}
                            </Link>
                          ) : 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                          Provider Type
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip 
                            label={viewingRequest.serviceProvider?.data?.attributes?.type || viewingRequest.attributes?.serviceProvider?.data?.attributes?.type} 
                            size="small" 
                            color="primary"
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                          Phone
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                          📞 {viewingRequest.serviceProvider?.data?.attributes?.phone || viewingRequest.attributes?.serviceProvider?.data?.attributes?.phone || 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                          Email
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                          ✉️ {viewingRequest.serviceProvider?.data?.attributes?.email || viewingRequest.attributes?.serviceProvider?.data?.attributes?.email || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                ) : (
                  <Alert severity="info">Not assigned yet - waiting for assignment</Alert>
                )}
              </Card>

              {/* WhatsApp Chat Buttons */}
              <Card variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Quick Actions
                </Typography>
                <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                  {(viewingRequest.insuranceClient?.data?.attributes?.phoneNumber || 
                    viewingRequest.attributes?.insuranceClient?.data?.attributes?.phoneNumber ||
                    viewingRequest.customer?.data?.attributes?.phone || 
                    viewingRequest.attributes?.customer?.data?.attributes?.phone) && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      onClick={() => {
                        const phone = viewingRequest.insuranceClient?.data?.attributes?.phoneNumber || 
                                     viewingRequest.attributes?.insuranceClient?.data?.attributes?.phoneNumber ||
                                     viewingRequest.customer?.data?.attributes?.phone || 
                                     viewingRequest.attributes?.customer?.data?.attributes?.phone;
                        if (phone) {
                          openWhatsAppChat(phone);
                        }
                      }}
                      fullWidth
                      sx={{ 
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        bgcolor: '#25D366',
                        '&:hover': { bgcolor: '#20BA5A' }
                      }}
                    >
                      Chat with Client
                    </Button>
                  )}
                  {(viewingRequest.serviceProvider?.data?.attributes?.phone || 
                    viewingRequest.attributes?.serviceProvider?.data?.attributes?.phone) && (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      onClick={() => {
                        const phone = viewingRequest.serviceProvider?.data?.attributes?.phone || 
                                     viewingRequest.attributes?.serviceProvider?.data?.attributes?.phone;
                        if (phone) {
                          openWhatsAppChat(phone);
                        }
                      }}
                      fullWidth
                      sx={{ 
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        bgcolor: '#25D366',
                        '&:hover': { bgcolor: '#20BA5A' }
                      }}
                    >
                      Chat with Service Provider
                    </Button>
                  )}
                </Stack>
              </Card>

              {/* Invoice Details */}
              <Card variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Invoice Information
                </Typography>
                {(() => {
                  const requestAttrs = viewingRequest.attributes || viewingRequest;
                  const invoices = requestAttrs.invoices?.data || requestAttrs.invoices || [];
                  
                  // Find customer and insurance invoices
                  const customerInvoice = invoices.find((inv: any) => {
                    const invAttrs = inv.attributes || inv;
                    return invAttrs.invoiceType === 'service_provider_to_darb';
                  });
                  const insuranceInvoice = invoices.find((inv: any) => {
                    const invAttrs = inv.attributes || inv;
                    return invAttrs.invoiceType === 'insurance_company_monthly';
                  });
                  
                  // Fallback to old single invoice relation
                  const legacyInvoice = viewingRequest.invoice?.data?.attributes || viewingRequest.attributes?.invoice?.data?.attributes;
                  
                  if (customerInvoice || insuranceInvoice || legacyInvoice) {
                    const customerInvoiceData = customerInvoice?.attributes || customerInvoice;
                    const insuranceInvoiceData = insuranceInvoice?.attributes || insuranceInvoice;
                    
                    return (
                      <Stack spacing={3}>
                        {/* Customer Invoice */}
                        {customerInvoiceData && (
                          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
                              Customer Invoice
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                  Invoice Number
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600, fontFamily: 'monospace' }}>
                                  {customerInvoiceData.invoiceNumber || '-'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                  Total Amount
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
                                  OMR {((customerInvoiceData.total || 0)).toFixed(3)}
                                </Typography>
                              </Box>
                            </Box>
                            {/* Invoice Items */}
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Items
                                </Typography>
                                <Button
                                  size="small"
                                  startIcon={<Add />}
                                  onClick={() => handleAddInvoiceItem('customer')}
                                  sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                                >
                                  Add Item
                                </Button>
                              </Box>
                              {customerInvoiceItems.length > 0 ? (
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  {customerInvoiceItems.map((item: any, idx: number) => {
                                    const itemAttrs = item.attributes || item;
                                    return (
                                      <Box key={item.id || idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', '&:hover': { bgcolor: 'action.hover' }, p: 0.5, borderRadius: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                            {itemAttrs.description || '-'}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                            Qty: {itemAttrs.quantity || 0} × OMR {(itemAttrs.unitPrice || 0).toFixed(3)}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', minWidth: '70px', textAlign: 'right' }}>
                                            OMR {(itemAttrs.total || 0).toFixed(3)}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleEditInvoiceItem(item, 'customer')}
                                            sx={{ p: 0.5 }}
                                          >
                                            <Edit sx={{ fontSize: '0.9rem' }} />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteInvoiceItem(item, 'customer')}
                                            sx={{ p: 0.5 }}
                                            color="error"
                                          >
                                            <Delete sx={{ fontSize: '0.9rem' }} />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              ) : (
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                  No items yet. Click "Add Item" to add one.
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Insurance Invoice */}
                        {insuranceInvoiceData && (
                          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
                              Insurance Invoice
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                  Invoice Number
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600, fontFamily: 'monospace' }}>
                                  {insuranceInvoiceData.invoiceNumber || '-'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                  Total Amount
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
                                  OMR {((insuranceInvoiceData.total || 0)).toFixed(3)}
                                </Typography>
                              </Box>
                            </Box>
                            {/* Invoice Items */}
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Items
                                </Typography>
                                <Button
                                  size="small"
                                  startIcon={<Add />}
                                  onClick={() => handleAddInvoiceItem('insurance')}
                                  sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
                                >
                                  Add Item
                                </Button>
                              </Box>
                              {insuranceInvoiceItems.length > 0 ? (
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                  {insuranceInvoiceItems.map((item: any, idx: number) => {
                                    const itemAttrs = item.attributes || item;
                                    return (
                                      <Box key={item.id || idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', '&:hover': { bgcolor: 'action.hover' }, p: 0.5, borderRadius: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                            {itemAttrs.description || '-'}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                            Qty: {itemAttrs.quantity || 0} × OMR {(itemAttrs.unitPrice || 0).toFixed(3)}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', minWidth: '70px', textAlign: 'right' }}>
                                            OMR {(itemAttrs.total || 0).toFixed(3)}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleEditInvoiceItem(item, 'insurance')}
                                            sx={{ p: 0.5 }}
                                          >
                                            <Edit sx={{ fontSize: '0.9rem' }} />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleDeleteInvoiceItem(item, 'insurance')}
                                            sx={{ p: 0.5 }}
                                            color="error"
                                          >
                                            <Delete sx={{ fontSize: '0.9rem' }} />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              ) : (
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                  No items yet. Click "Add Item" to add one.
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        
                        {/* Legacy single invoice (fallback) */}
                        {!customerInvoiceData && !insuranceInvoiceData && legacyInvoice && (
                          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                  Invoice Number
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600, fontFamily: 'monospace' }}>
                                  {legacyInvoice.invoiceNumber || '-'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                                  Total Amount
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
                                  OMR {((legacyInvoice.total || 0)).toFixed(3)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                        
                        {/* Download Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<PdfIcon />}
                            onClick={() => handleDownloadInvoicePDF('customer')}
                            sx={{ minWidth: 180 }}
                            disabled={!customerInvoiceData}
                          >
                            Customer Invoice
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PdfIcon />}
                            onClick={() => handleDownloadInvoicePDF('insurance')}
                            sx={{ minWidth: 180 }}
                            disabled={!insuranceInvoiceData}
                          >
                            Insurance Invoice
                          </Button>
                        </Box>
                      </Stack>
                    );
                  }
                  
                  return <Alert severity="info">No invoice generated yet</Alert>;
                })()}
              </Card>

              {/* Timeline */}
              <Card variant="outlined" sx={{ p: 3, bgcolor: 'background.default' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Timeline
                </Typography>
                <Stack spacing={1.5}>
                  {(viewingRequest.createdAt || viewingRequest.attributes?.createdAt) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="600">Created</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(viewingRequest.createdAt || viewingRequest.attributes?.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {(viewingRequest.assignedAt || viewingRequest.attributes?.assignedAt) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="600">Assigned</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(viewingRequest.assignedAt || viewingRequest.attributes?.assignedAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {(viewingRequest.invoiceSentAt || viewingRequest.attributes?.invoiceSentAt) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'secondary.main' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="600">Invoice Sent</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(viewingRequest.invoiceSentAt || viewingRequest.attributes?.invoiceSentAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {(viewingRequest.paymentReceivedAt || viewingRequest.attributes?.paymentReceivedAt) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="600">Payment Received</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(viewingRequest.paymentReceivedAt || viewingRequest.attributes?.paymentReceivedAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {(viewingRequest.completedDate || viewingRequest.attributes?.completedDate) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="600" color="success.main">Completed</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(viewingRequest.completedDate || viewingRequest.attributes?.completedDate).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {(viewingRequest.updatedAt || viewingRequest.attributes?.updatedAt) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.400' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="600">Last Updated</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(viewingRequest.updatedAt || viewingRequest.attributes?.updatedAt).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Card>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button onClick={handleDetailsClose} color="inherit">
            Close
          </Button>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {viewingRequest && (
              <>
                {/* Assign Client buttons for NOT OUR CLIENT requests */}
                {(() => {
                  const attrs = viewingRequest.attributes || viewingRequest;
                  const isNotOurClient = attrs.isNotOurClient || false;
                  if (isNotOurClient) {
                    return (
                      <>
                        <Button
                          onClick={() => handleAssignClientOpen(viewingRequest)}
                          variant="contained"
                          color="primary"
                          startIcon={<Add />}
                        >
                          Create New Insurance Client
                        </Button>
                        <Button
                          onClick={() => handleAssignClientOpen(viewingRequest)}
                          variant="outlined"
                          color="primary"
                          startIcon={<SwapHoriz />}
                        >
                          Assign Client
                        </Button>
                      </>
                    );
                  }
                  return null;
                })()}
                {/* Assign button for pending requests */}
                {(viewingRequest.status === 'pending' || viewingRequest.attributes?.status === 'pending') && 
                 !viewingRequest.serviceProvider?.data && !viewingRequest.attributes?.serviceProvider?.data && (
                  <Button
                    onClick={() => handleAssignDialogOpen(viewingRequest)}
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircle />}
                  >
                    Assign Service Provider
                  </Button>
                )}
                
                {/* Workflow progression buttons */}
                {(viewingRequest.status === 'assigned' || viewingRequest.attributes?.status === 'assigned') && (
                  <Button
                    onClick={async () => {
                      await handleStatusChange(viewingRequest.id, 'active');
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircle />}
                  >
                    Start Work (Mark Active)
                  </Button>
                )}
                {(viewingRequest.status === 'active' || viewingRequest.attributes?.status === 'active') && (
                  <Button
                    onClick={() => {
                      setRequestForInvoice(viewingRequest);
                      setInvoiceModalOpen(true);
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircle />}
                  >
                    Send Invoice
                  </Button>
                )}
                {(viewingRequest.status === 'invoice_sent' || viewingRequest.attributes?.status === 'invoice_sent') && (
                  Number((viewingRequest as any).extraKilometers || viewingRequest.attributes?.extraKilometers) === 0 ? (
                    <Button
                      onClick={async () => {
                        try {
                          await requestService.update(viewingRequest.id, {
                            status: 'payment_received',
                            paymentStatus: 'paid',
                            paymentReceivedAt: new Date().toISOString(),
                            completedDate: new Date().toISOString()
                          } as any);
                          await requestService.update(viewingRequest.id, {
                            status: 'completed'
                          } as any);
                          loadRequests();
                        } catch (err: any) {
                          setError(err.response?.data?.error?.message || 'Failed to complete request');
                        }
                        handleDetailsClose();
                      }}
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                    >
                      Complete (No Payment Needed)
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setRequestForPayment(viewingRequest);
                        setPaymentModalOpen(true);
                      }}
                      variant="contained"
                      color="primary"
                      startIcon={<CheckCircle />}
                    >
                      Confirm Payment
                    </Button>
                  )
                )}
                {(viewingRequest.status === 'payment_received' || viewingRequest.attributes?.status === 'payment_received') && (
                  <Button
                    onClick={async () => {
                      await handleStatusChange(viewingRequest.id, 'completed');
                      handleDetailsClose();
                    }}
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                  >
                    Complete Request
                  </Button>
                )}

                {/* Change provider button for active requests */}
                {(viewingRequest.status !== 'pending' && viewingRequest.status !== 'completed' && viewingRequest.status !== 'cancelled') && (
                  <Button
                    onClick={() => handleChangeProviderOpen(viewingRequest)}
                    variant="outlined"
                    color="warning"
                    startIcon={<SwapHoriz />}
                  >
                    Change Provider
                  </Button>
                )}
                
                {/* Cancel button for non-completed requests */}
                {(viewingRequest.status !== 'completed' && viewingRequest.status !== 'cancelled' &&
                  viewingRequest.attributes?.status !== 'completed' && viewingRequest.attributes?.status !== 'cancelled') && (
                  <Button
                    onClick={async () => {
                      await handleStatusChange(viewingRequest.id, 'cancelled');
                      handleDetailsClose();
                    }}
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                  >
                    Cancel Request
                  </Button>
                )}
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Assign Provider Dialog */}
      <Dialog open={assignDialogOpen} onClose={handleAssignDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Service Provider</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Request</Typography>
              <Typography variant="body1">
                {assigningRequest?.attributes?.requestNumber || assigningRequest?.requestNumber || `REQ-${assigningRequest?.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assigningRequest?.attributes?.description || assigningRequest?.description}
              </Typography>
            </Box>
            <Autocomplete
              fullWidth
              options={serviceProviders}
              value={providerToAssign}
              onChange={(_, newValue) => setProviderToAssign(newValue)}
              getOptionLabel={(option) => {
                const attrs = option.attributes || option;
                return attrs.name || `Provider ${option.id}`;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Service Provider *"
                  placeholder="Search provider..."
                  required
                />
              )}
              renderOption={(props, option) => {
                const attrs = option.attributes || option;
                const isActive = attrs.isActive ?? true;
                const location = attrs.area || attrs.address || 'No location';
                const schedule = formatSchedule(attrs.schedule);
                
                return (
                  <li {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" fontWeight={isActive ? 600 : 400}>
                          {attrs.name}
                        </Typography>
                        <Chip 
                          label={isActive ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={isActive ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {attrs.type} - {attrs.phone}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        📍 {location}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        🕐 {schedule}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No service providers found"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignDialogClose}>Cancel</Button>
          <Button onClick={handleAssignProvider} variant="contained" color="success">
            Assign Provider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Provider Dialog */}
      <Dialog open={changeProviderOpen} onClose={handleChangeProviderClose} maxWidth="sm" fullWidth>
        <DialogTitle>Change Service Provider</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Current Provider</Typography>
              <Typography variant="body1">
                {changingRequest?.attributes?.serviceProvider?.data?.attributes?.name || changingRequest?.serviceProvider?.data?.attributes?.name || 'N/A'}
              </Typography>
            </Box>
            <Autocomplete
              fullWidth
              options={serviceProviders}
              value={newProvider}
              onChange={(_, newValue) => setNewProvider(newValue)}
              getOptionLabel={(option) => {
                const attrs = option.attributes || option;
                return attrs.name || `Provider ${option.id}`;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select New Service Provider *"
                  placeholder="Search provider..."
                  required
                />
              )}
              renderOption={(props, option) => {
                const attrs = option.attributes || option;
                const isActive = attrs.isActive ?? true;
                const location = attrs.area || attrs.address || 'No location';
                const schedule = formatSchedule(attrs.schedule);
                
                return (
                  <li {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" fontWeight={isActive ? 600 : 400}>
                          {attrs.name}
                        </Typography>
                        <Chip 
                          label={isActive ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={isActive ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {attrs.type} - {attrs.phone}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        📍 {location}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        🕐 {schedule}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText="No service providers found"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChangeProviderClose}>Cancel</Button>
          <Button onClick={handleChangeProvider} variant="contained" color="primary">
            Change Provider
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Client Modal */}
      <Dialog open={assignClientModalOpen} onClose={handleAssignClientClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {creatingNewClient ? 'Create New Insurance Client & Assign to Request' : 'Assign Client to Request'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Request</Typography>
              <Typography variant="body1">
                {assigningRequestForClient?.attributes?.requestNumber || assigningRequestForClient?.requestNumber || `REQ-${assigningRequestForClient?.id}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assigningRequestForClient?.attributes?.description || assigningRequestForClient?.description}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant={!creatingNewClient ? 'contained' : 'outlined'}
                onClick={() => setCreatingNewClient(false)}
                fullWidth
              >
                Assign Existing Client
              </Button>
              <Button
                variant={creatingNewClient ? 'contained' : 'outlined'}
                onClick={() => setCreatingNewClient(true)}
                fullWidth
              >
                Create New Client
              </Button>
            </Box>

            {!creatingNewClient ? (
              // Assign Existing Client
              <Autocomplete
                fullWidth
                options={insuranceClients}
                value={selectedClientToAssign}
                onChange={(_, newValue) => setSelectedClientToAssign(newValue)}
                getOptionLabel={(option) => {
                  const attrs = option.attributes || option;
                  const name = attrs.customerName || '';
                  const vin = attrs.vinNumber || '';
                  return name && vin ? `${name} (${vin})` : name || vin || `Client ${option.id}`;
                }}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  const searchLower = inputValue.toLowerCase();
                  return options.filter((option) => {
                    const attrs = option.attributes || option;
                    const name = (attrs.customerName || '').toLowerCase();
                    const vin = (attrs.vinNumber || '').toLowerCase();
                    const phone = (attrs.phoneNumber || '').toLowerCase();
                    return name.includes(searchLower) || vin.includes(searchLower) || phone.includes(searchLower);
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Insurance Client *"
                    placeholder="Search by name, VIN, or phone..."
                    required
                  />
                )}
                renderOption={(props, option) => {
                  const attrs = option.attributes || option;
                  return (
                    <li {...props} key={option.id}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body1" fontWeight={600}>
                          {attrs.customerName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          VIN: {attrs.vinNumber || 'N/A'}
                        </Typography>
                        {attrs.phoneNumber && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Phone: {attrs.phoneNumber}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                noOptionsText="No clients found"
              />
            ) : (
              // Create New Client Form
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="VIN Number *"
                  value={newClientFormData.vinNumber}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, vinNumber: e.target.value.toUpperCase() })}
                  required
                  inputProps={{ maxLength: 17 }}
                />
                <TextField
                  fullWidth
                  label="Customer Name *"
                  value={newClientFormData.customerName}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, customerName: e.target.value })}
                  required
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={newClientFormData.phoneNumber}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, phoneNumber: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newClientFormData.email}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, email: e.target.value })}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Plate Number"
                    value={newClientFormData.plateNumber}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, plateNumber: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Car Make"
                    value={newClientFormData.carMake}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, carMake: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Car Model"
                    value={newClientFormData.carModel}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, carModel: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Car Year"
                    value={newClientFormData.carYear}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, carYear: e.target.value })}
                  />
                </Box>
                <TextField
                  fullWidth
                  label="Policy Number"
                  value={newClientFormData.policyNumber}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, policyNumber: e.target.value })}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Policy Start Date"
                    type="date"
                    value={newClientFormData.policyStartDate}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, policyStartDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    label="Policy End Date"
                    type="date"
                    value={newClientFormData.policyEndDate}
                    onChange={(e) => setNewClientFormData({ ...newClientFormData, policyEndDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                <Autocomplete
                  fullWidth
                  options={insuranceCompanies}
                  value={insuranceCompanies.find(c => {
                    const id = c.id || c.attributes?.id || c.data?.id;
                    return id?.toString() === newClientFormData.insuranceCompany?.toString();
                  }) || null}
                  onChange={(_, newValue) => {
                    const id = newValue?.id || newValue?.attributes?.id || newValue?.data?.id;
                    setNewClientFormData({ ...newClientFormData, insuranceCompany: id || '' });
                  }}
                  getOptionLabel={(option) => {
                    const attrs = option.attributes || option.data?.attributes || option;
                    return attrs.name || `Company ${option.id}`;
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Insurance Company" placeholder="Select company..." />
                  )}
                />
                <Autocomplete
                  fullWidth
                  options={servicePackages}
                  value={servicePackages.find(p => {
                    const id = p.id || p.attributes?.id || p.data?.id;
                    return id?.toString() === newClientFormData.servicePackage?.toString();
                  }) || null}
                  onChange={(_, newValue) => {
                    const id = newValue?.id || newValue?.attributes?.id || newValue?.data?.id;
                    setNewClientFormData({ ...newClientFormData, servicePackage: id || '' });
                  }}
                  getOptionLabel={(option) => {
                    const attrs = option.attributes || option.data?.attributes || option;
                    return attrs.name || `Package ${option.id}`;
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Service Package" placeholder="Select package..." />
                  )}
                />
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={newClientFormData.notes}
                  onChange={(e) => setNewClientFormData({ ...newClientFormData, notes: e.target.value })}
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignClientClose} disabled={assigningClient}>
            Cancel
          </Button>
          {!creatingNewClient ? (
            <Button
              onClick={handleAssignExistingClient}
              variant="contained"
              color="primary"
              disabled={!selectedClientToAssign || assigningClient}
            >
              {assigningClient ? 'Assigning...' : 'Assign Client'}
            </Button>
          ) : (
            <Button
              onClick={handleCreateAndAssignClient}
              variant="contained"
              color="primary"
              disabled={!newClientFormData.vinNumber || !newClientFormData.customerName || assigningClient}
            >
              {assigningClient ? 'Creating...' : 'Create & Assign Client'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Create Invoice Modal */}
      {requestForInvoice && (
        <CreateInvoiceModal
          open={invoiceModalOpen}
          onClose={() => {
            setInvoiceModalOpen(false);
            setRequestForInvoice(null);
          }}
          request={requestForInvoice}
          onSuccess={async () => {
            loadRequests();
            // Reload request details to show the newly created invoice
            if (requestForInvoice?.id) {
              await reloadRequestDetails(requestForInvoice.id);
            }
            // Keep the modal open to show the invoice
          }}
        />
      )}

      {/* Add Payment Modal */}
      {requestForPayment && (
        <AddPaymentModal
          open={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setRequestForPayment(null);
          }}
          request={requestForPayment}
          onSuccess={() => {
            loadRequests();
            handleDetailsClose();
          }}
        />
      )}

      {/* Import Modal */}
      <Dialog open={importModalOpen} onClose={() => !importing && setImportModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Requests</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import requests from Excel file or paste tab-separated data. Expected columns:
              <br />
              Date | Policy number | Vehicle type | Vehicle model | Plate Number | Customer Number | Location (from) | Location (to) | Driver Name | Service type | Breakdown details | OMR 0.000 | REGISTERED BY
            </Typography>
            
            {/* Import Mode Selection */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant={importMode === 'text' ? 'contained' : 'outlined'}
                onClick={() => {
                  setImportMode('text');
                  setImportFile(null);
                  setError('');
                }}
                disabled={importing}
              >
                Paste Text
              </Button>
              <Button
                variant={importMode === 'excel' ? 'contained' : 'outlined'}
                onClick={() => {
                  setImportMode('excel');
                  setImportDataText('');
                  setError('');
                }}
                disabled={importing}
              >
                Upload Excel
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {importMode === 'text' ? (
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Import Data"
                placeholder="Paste your data here (tab-separated values)"
                value={importDataText}
                onChange={(e) => {
                  setImportDataText(e.target.value);
                  setError(''); // Clear error when user types
                }}
                disabled={importing}
                sx={{ mb: 2 }}
              />
            ) : (
              <Box sx={{ mb: 2 }}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setImportFile(file);
                      setError('');
                    }
                  }}
                  style={{ display: 'none' }}
                  id="excel-upload-input"
                  disabled={importing}
                />
                <label htmlFor="excel-upload-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadFile />}
                    fullWidth
                    disabled={importing}
                    sx={{ mb: 1 }}
                  >
                    {importFile ? importFile.name : 'Select Excel File (.xlsx, .xls)'}
                  </Button>
                </label>
                {importFile && (
                  <Typography variant="caption" color="text.secondary">
                    Selected: {importFile.name} ({(importFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                )}
              </Box>
            )}
            {importResult && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={importResult.summary?.failed === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Import Summary</Typography>
                  <Typography variant="body2">
                    Total: {importResult.summary?.total || 0} | 
                    Successful: {importResult.summary?.successful || 0} | 
                    Failed: {importResult.summary?.failed || 0}
                  </Typography>
                </Alert>
                {importResult.results?.errors && importResult.results.errors.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="error">Errors:</Typography>
                    {importResult.results.errors.slice(0, 10).map((err: any, idx: number) => (
                      <Typography key={idx} variant="body2" color="error">
                        Row {err.row}: {err.error}
                      </Typography>
                    ))}
                    {importResult.results.errors.length > 10 && (
                      <Typography variant="body2" color="text.secondary">
                        ... and {importResult.results.errors.length - 10} more errors
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setImportModalOpen(false);
            setImportResult(null);
            setImportDataText('');
            setImportFile(null);
            setImportMode('text');
          }} disabled={importing}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                setImporting(true);
                setError('');
                setImportResult(null);

                let importData: any[] = [];

                if (importMode === 'excel') {
                  if (!importFile) {
                    setError('Please select an Excel file');
                    setImporting(false);
                    return;
                  }

                  // Parse Excel file
                  importData = await parseExcelFile(importFile);
                } else {
                  // Parse text data
                  const data = importDataText.trim();
                  
                  if (!data) {
                    setError('Please paste import data');
                    setImporting(false);
                    return;
                  }

                  importData = parseTextData(data);
                }

                if (importData.length === 0) {
                  setError('No valid data rows found');
                  setImporting(false);
                  return;
                }

                // Close import modal and open progress dialog
                setImportModalOpen(false);
                setProgressDialogOpen(true);

                // Use SSE helper to handle streaming import
                const url = requestService.getImportStreamUrl();
                await handleSSEImport(url, importData, {
                  onEvent: (event) => {
                    // Pass event to progress dialog
                    if (progressDialogRef.current && progressDialogRef.current.handleEvent) {
                      progressDialogRef.current.handleEvent(event);
                    }

                    // Refresh request list when complete
                    if (event.type === 'complete') {
                      loadRequests();
                      setImportDataText('');
                      setImportFile(null);
                      setImportResult(event.data.results);
                    }
                  },
                  onError: (error) => {
                    console.error('Import error:', error);
                    setError(error.message);
                    setProgressDialogOpen(false);
                  },
                  onComplete: () => {
                    console.log('Import stream completed');
                  }
                });
                
              } catch (error: any) {
                console.error('Import error:', error);
                setError(error.response?.data?.error?.message || error.message || 'Failed to import requests');
                setProgressDialogOpen(false);
              } finally {
                setImporting(false);
              }
            }}
            disabled={importing || (importMode === 'text' && !importDataText.trim()) || (importMode === 'excel' && !importFile)}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Invoice Item Modal */}
      <Dialog 
        open={editInvoiceItemModalOpen} 
        onClose={() => !savingInvoiceItem && setEditInvoiceItemModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingInvoiceItem ? 'Edit Invoice Item' : 'Add Invoice Item'}
          {editingInvoiceType && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {editingInvoiceType === 'customer' ? 'Customer Invoice' : 'Insurance Invoice'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Description"
              value={editItemFormData.description}
              onChange={(e) => setEditItemFormData({ ...editItemFormData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              required
            />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <NumberTextField
                label="Quantity"
                value={editItemFormData.quantity}
                onChange={(e) => setEditItemFormData({ ...editItemFormData, quantity: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
                required
                allowDecimal={false}
              />
              <NumberTextField
                label="Unit Price (OMR)"
                value={editItemFormData.unitPrice}
                onChange={(e) => setEditItemFormData({ ...editItemFormData, unitPrice: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.001 }}
                required
              />
            </Box>
            
            <NumberTextField
              label="Discount (OMR)"
              value={editItemFormData.discount}
              onChange={(e) => setEditItemFormData({ ...editItemFormData, discount: parseFloat(e.target.value) || 0 })}
              inputProps={{ min: 0, step: 0.001 }}
              fullWidth
            />
            
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editItemFormData.apply5PercentTax}
                    onChange={(e) => setEditItemFormData({ 
                      ...editItemFormData, 
                      apply5PercentTax: e.target.checked,
                      tax: e.target.checked ? 0 : editItemFormData.tax
                    })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Apply 5% Tax Automatically</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tax will be calculated as 5% of (Subtotal - Discount)
                    </Typography>
                  </Box>
                }
              />
              
              {!editItemFormData.apply5PercentTax && (
                <NumberTextField
                  label="Tax (OMR)"
                  value={editItemFormData.tax}
                  onChange={(e) => setEditItemFormData({ ...editItemFormData, tax: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, step: 0.001 }}
                  fullWidth
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
            
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Calculation
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal:
                  </Typography>
                  <Typography variant="body2">
                    OMR {calculateItemTotal().subtotal.toFixed(3)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Discount:
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    - OMR {(editItemFormData.discount || 0).toFixed(3)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    After Discount:
                  </Typography>
                  <Typography variant="body2">
                    OMR {calculateItemTotal().afterDiscount.toFixed(3)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Tax {editItemFormData.apply5PercentTax && '(5%)'}:
                  </Typography>
                  <Typography variant="body2">
                    + OMR {calculateItemTotal().tax.toFixed(3)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight="bold">
                    Total:
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    OMR {calculateItemTotal().total.toFixed(3)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditInvoiceItemModalOpen(false)}
            disabled={savingInvoiceItem}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveInvoiceItem}
            variant="contained"
            disabled={savingInvoiceItem || !editItemFormData.description.trim()}
          >
            {savingInvoiceItem ? 'Saving...' : (editingInvoiceItem ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Progress Dialog */}
      <UploadProgressDialog
        open={progressDialogOpen}
        onClose={() => setProgressDialogOpen(false)}
        title="Requests Import Progress"
        ref={progressDialogRef}
      />
    </Box>
  );
};

export default RequestList;

